import React, {
    useState,
    useEffect,
    useLayoutEffect,
    useCallback,
    useRef,
    useMemo,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    Typography,
} from '@mui/material';
import { useOptimizer } from '../hooks/useOptimizer';
import {
    formatUserFactorModel,
    getEffectiveFactorDataModel,
    normalizeAxiomaFactorDataModel,
    normalizeBarraFactorDataModel,
    normalizeFactorDataModel,
    normalizeUserFactorModel,
} from '../utils/factorDataOptions';
import {
    getAvailableBetaSources,
    isBetaSourceAvailable,
    normalizeBetaSource,
} from '../utils/betaSources';
import { useAppContext } from '../context/AppContext';
import usePortfolioImports from './dashboard/hooks/usePortfolioImports';
import usePriceRefreshStatus from './dashboard/hooks/usePriceRefreshStatus';
import usePortfolioRiskConfig from './dashboard/hooks/usePortfolioRiskConfig';
import useDashboardAppBarBridge from './dashboard/hooks/useDashboardAppBarBridge';
import usePosSizingSync from './dashboard/hooks/usePosSizingSync';
import usePosSizingSyncPreferences from './dashboard/hooks/usePosSizingSyncPreferences';
import usePosSizingFreshnessPrompt from './dashboard/hooks/usePosSizingFreshnessPrompt';
import useWatchlistWorkflow from './dashboard/hooks/useWatchlistWorkflow';
import usePriceRefreshFlow from './dashboard/hooks/usePriceRefreshFlow';
import useCoreLoadBootstrap, { coreLoadPublicKey } from './dashboard/hooks/useCoreLoadBootstrap';
import useImportApplySync from './dashboard/hooks/useImportApplySync';
import useOverviewSelectionRefresh from './dashboard/hooks/useOverviewSelectionRefresh';
import useLatestState from './dashboard/hooks/useLatestState';
import { buildActualRiskOverviewScope, createPublicationVersionTransition } from './dashboard/orchestration/publicationVersionTransition';
import useOverviewPriceFreshness from './dashboard/hooks/useOverviewPriceFreshness';
import useBetaContextRefresh from './dashboard/hooks/useBetaContextRefresh';
import usePosSizingDraftConsumer from './dashboard/hooks/usePosSizingDraftConsumer';
import useOverviewSyncControls from './dashboard/hooks/useOverviewSyncControls';
import useCombinedHoldings, { combineHoldings } from './dashboard/hooks/useCombinedHoldings';
import useRiskOverviewReadinessPolling from './dashboard/hooks/useRiskOverviewReadinessPolling';
import useOptimizerPortfolioDropdownBootstrap from './dashboard/hooks/useOptimizerPortfolioDropdownBootstrap';
import useOptimizerIdioSourceChange from './dashboard/hooks/useOptimizerIdioSourceChange';
import usePortfolioBaseDataFetchers from './dashboard/hooks/usePortfolioBaseDataFetchers';
import useTradeIdeasWorkflow from './dashboard/hooks/useTradeIdeasWorkflow';
import useOnDemandActualTradeIdeas from './dashboard/hooks/useOnDemandActualTradeIdeas';
import useCoreLoadOrchestrator from './dashboard/hooks/useCoreLoadOrchestrator';
import useQueuedPriceRefresh from './dashboard/hooks/useQueuedPriceRefresh';
import usePosSizingPersistence from './dashboard/hooks/usePosSizingPersistence';
import usePosSizingStartingValues from './dashboard/hooks/usePosSizingStartingValues';
import useRiskDataFetchers from './dashboard/hooks/useRiskDataFetchers';
import useFactorPositionDetails from './dashboard/hooks/useFactorPositionDetails';
import useFactorMatrixWorkflow from './dashboard/hooks/useFactorMatrixWorkflow';
import useFactorDataFreshnessPolling from './dashboard/hooks/useFactorDataFreshnessPolling';
import usePosSizingDataFetcher from './dashboard/hooks/usePosSizingDataFetcher';
import useLiveQuotesPolling from './dashboard/hooks/useLiveQuotesPolling';
import useDashboardUploadControls from './dashboard/hooks/useDashboardUploadControls';
import useWatchlistContextSync from './dashboard/hooks/useWatchlistContextSync';
import useFactorDatasetCatalogue from './dashboard/hooks/useFactorDatasetCatalogue';
import useFactorDataSyncPreferences from './dashboard/hooks/useFactorDataSyncPreferences';
import FactorDataUpdateDialog from './dashboard/components/FactorDataUpdateDialog';
import { factorDataDisplayContext, factorDataUpdateExplanation, hasFactorDataUpdate } from '../context/factorDataSyncPolicy';
import useWatchlistNews from '../hooks/useWatchlistNews';
import useSecurityNewsDetails from '../hooks/useSecurityNewsDetails';
import useWatchlistReviewNavigationHandoff from '../hooks/useWatchlistReviewNavigationHandoff';
import useOptimizerPortfolioSummaries from './dashboard/hooks/useOptimizerPortfolioSummaries';
import useRiskFactorsHeaderActionsConfig from './dashboard/hooks/useRiskFactorsHeaderActionsConfig';
import useRiskOrchestrationCommands, { RISK_INVALIDATION_CAUSES } from './dashboard/orchestration/useRiskOrchestrationCommands';
import { getRiskCommandsOrThrow } from './dashboard/orchestration/riskCommandRef';
import { buildRiskOverviewScopeKey } from './dashboard/riskOverviewResource';
import {
    POS_SIZING_PORTFOLIO_LABEL,
    buildOverviewRiskContextInputs,
    buildPortfolioOverviewRiskContext,
    findSelectedOverviewPortfolio,
    isPosSizingOverviewSelection,
    resolveActiveFactorSelection,
    resolveSelectedOverviewScope,
    selectedOverviewPortfolioChanged,
} from './dashboard/selectedOverviewScope';
import PerformancePendingImportBanner from './dashboard/components/PerformancePendingImportBanner';
import PerformancePendingImportDialog from './dashboard/components/PerformancePendingImportDialog';
import RiskFactorsHeaderActions from './dashboard/components/RiskFactorsHeaderActions';
import DashboardWatchlistSection from './dashboard/components/DashboardWatchlistSection';
import DashboardPosSizingSection from './dashboard/components/DashboardPosSizingSection';
import PosSizingStartingValuesDialog from './pos_sizing_table/PosSizingStartingValuesDialog';
import SizingDefaultsControl from './pos_sizing_table/SizingDefaultsControl';
import PersistentDashboardView from './dashboard/components/PersistentDashboardView';
import DashboardSnackbars from './dashboard/components/DashboardSnackbars';
import DashboardOverviewSection from './dashboard/components/DashboardOverviewSection';
import DashboardOptimizerSection from './dashboard/components/DashboardOptimizerSection';
import DashboardPerformanceSection from './dashboard/components/DashboardPerformanceSection';
import DashboardLoadedFactorChip from './dashboard/components/DashboardLoadedFactorChip';
import useLoadedCoreFactorSlice from './dashboard/hooks/useLoadedCoreFactorSlice';
import { invalidateLoadedWorkspaceFactorSlice } from '../utils/loadedFactorSlice';
import DraftFreshnessHeaderControl from './pos_sizing_table/DraftFreshnessHeaderControl';
import { resolveDashboardActiveView } from './dashboard/components/DashboardPageHeader';
import MsiOutboundBarraResolutionDialog from './MsiOutboundBarraResolutionDialog';
import OptimizerWatchlistRestorationDialog, {
    optimizerWatchlistRestorationRequired,
    optimizerWatchlistSelectionNeedsResolution,
} from './optimizer/OptimizerWatchlistRestorationDialog';
import {
    APPBAR_UPLOAD_MODAL_OPEN_EVENT,
    ENABLE_HOLDINGS_CALCULATION,
    ENABLE_INDUSTRIES_FETCH,
    ENABLE_POSITIONS_FETCH,
    OVERVIEW_QUOTE_POLL_MS,
    WATCHLIST_QUOTE_POLL_MS,
    buildTrackedQuoteIdentities,
    buildFactorContextFromStatus,
    buildFactorDataFreshnessSummary,
    formatFactorDataContextSummary,
    buildPosSizingDraftFreshnessSummary,
    getPosSizingActualSourceBlocker,
    factorContextsUseSameFactorGeneration,
    factorContextsUseSameFactorDataSnapshot,
    factorContextsUseSameSelection,
    getFactorContextKey,
    inheritFactorGenerationDigest,
    isFactorContextLoadable,
    isPendingFactorMatrixRefreshResult,
    isPublishedFactorSettingAwaitingAcceptance,
    isReadyFactorMatrixRefreshResult,
    normalizeIdioVolSource,
    resolveDashboardPageError,
    shouldPromoteVerifiedFactorContext,
} from './dashboard/dashboardUtils';
import {
    MSI_SECURITY_RESOLUTION_APPLIED_EVENT,
    WATCHLIST_DELTA_CLASSIFICATIONS,
    requiresWatchlistClientPublication,
    isSecurityOperationEventForScope,
    parseWatchlistDeltaClassification,
} from '../utils/securityResolutionEvents';
import {
    isSecurityOperationActive,
    watchlistUpdateOutcomeNotice,
} from '../utils/securityOperationStatus';
import {
    parseWatchlistPublicationReceipt,
} from '../utils/watchlistPublication';
import { normalizeWatchlistLifecycle } from '../utils/watchlistLifecycle';
import { deriveMsiContextStatus } from '../context/applicationContextPresentation';
import useOptimizerWatchlistRecovery from './dashboard/hooks/useOptimizerWatchlistRecovery';
import usePublishedFactorSettingFollow from './dashboard/hooks/usePublishedFactorSettingFollow';
import { fetchWatchlistContextStatus } from '../utils/ApiCall';

function Dashboard({
    user,
    userData,
    onUserDataChange,
    compactMode,
    newsRecencyHours,
    deepDiveFocusAreas,
    maxSharpeSkill = null,
    setMaxSharpeSkill = null,
    compareExploreColumns = null,
    setCompareExploreColumns = null,
    securityOperationStatus = null,
    watchlistPublicationState = null,
    onRegisterWatchlistPublicationExecutor = null,
    onCommitWatchlistBootstrapRevision = null,
    securityOperationSessionScopeToken = '',
    exclusiveCoreLoadOwnerRef,
}) {
    const [holdings, setHoldings] = useState([]);

    const loadedFactorScopeKey = `${user?.uid || ''}|${securityOperationSessionScopeToken || ''}`;
    const loadedFactorScopeKeyRef = useRef(loadedFactorScopeKey);
    loadedFactorScopeKeyRef.current = loadedFactorScopeKey;
    const visiblePerformanceContextRef = useRef({ scopeKey: loadedFactorScopeKey, context: null });
    if (visiblePerformanceContextRef.current.scopeKey !== loadedFactorScopeKey) {
        visiblePerformanceContextRef.current = { scopeKey: loadedFactorScopeKey, context: null };
    }
    const mountedPublicationPreparationRef = useRef(null);
    const registerPublicationPreparation = useCallback((prepare) => {
        mountedPublicationPreparationRef.current = typeof prepare === 'function' ? prepare : null;
    }, []);
    const { commit: commitLoadedCoreFactorSlice, context: loadedCoreFactorContext,
        resultLoaded: loadedCoreFactorResult } = useLoadedCoreFactorSlice(loadedFactorScopeKey);
    const [calculations, setCalculations] = useState(null);

    const [optimizedRiskMetrics, setOptimizedRiskMetrics] = useState({});

    const [error, setError] = useState(null);
    const [holdingsLoading, setHoldingsLoading] = useState(true);
    const [calculationsLoading, setCalculationsLoading] = useState(false);
    const [positions, setPositions] = useState([]);
    const [positionsLoading, setPositionsLoading] = useState(false);
    const [userPositions, setUserPositions] = useState([]);

    const [industries, setIndustries] = useState([]);
    const [industriesLoading, setIndustriesLoading] = useState(true);
    const [watchlistAttributeSnapshot, setWatchlistAttributeSnapshot] = useState(null);
    const [watchlistPortfolioEditor, setWatchlistPortfolioEditor] = useState(null);
    const [portfolioEditorDialogOpen, setPortfolioEditorDialogOpen] = useState(false);
    const [watchlistUploadBusy, setWatchlistUploadBusy] = useState(false);
    const [liveQuotesByCogniveId, setLiveQuotesByCogniveId] = useState({});
    const [liveQuotesLastUpdatedAt, setLiveQuotesLastUpdatedAt] = useState(null);
    const [overviewSnapshotAt, setOverviewSnapshotAt] = useState(null);
    const liveQuotesByCogniveIdRef = useRef({});

    const [factorNames, setFactorNames] = useState([]);

    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(50);

    const [posSizingData, setPosSizingData] = useState([]);
    const [posSizingVisualizationRows, setPosSizingVisualizationRows] = useState([]);
    const [posSizingLinkedRowId, setPosSizingLinkedRowId] = useState(null);
    const [posSizingLinkedRowRequestId, setPosSizingLinkedRowRequestId] = useState(0);
    const [posSizingLoading, setPosSizingLoading] = useState(false);
    const [posSizingLoaded, setPosSizingLoaded] = useState(false);
    const [posSizingAttributeWorkspaceHydration, setPosSizingAttributeWorkspaceHydration] =
        useState(null);
    const [posSizingDraftSyncLoading, setPosSizingDraftSyncLoading] = useState(false);

    const [matricesLoading, setMatricesLoading] = useState(false);

    const [groupRisksLoading, setGroupRisksLoading] = useState(false);
    const [groupRiskMetrics, setGroupRiskMetrics] = useState(null);
    const [optimizedGroupRiskMetrics, setOptimizedGroupRiskMetrics] = useState(null);
    const [optimizedGroupRiskMetricsLoading, setOptimizedGroupRiskMetricsLoading] = useState(false);

    // The draft's settings, or the member's starting values without a draft;
    // both come from the backend load step (usePortfolioRiskConfig).
    const [maxPosSize, setMaxPosSize] = useState(null);
    const [normalVol, setNormalVol] = useState(null);
    const [aum, setAum] = useState(null);
    const [gmv, setGmv] = useState(null);
    const [maxPosSizeNormalVolLoading, setMaxPosSizeNormalVolLoading] = useState(false);
    const [maxPosSizeNormalVolLoaded, setMaxPosSizeNormalVolLoaded] = useState(false);
    const [aumLoading, setAumLoading] = useState(false);
    const [aumLoaded, setAumLoaded] = useState(false);

    const displayUnit = 'nominal';

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('error');
    const [overviewOptimizerRestorationPortfolio, setOverviewOptimizerRestorationPortfolio] =
        useState(null);
    const [aumSnackOpen, setAumSnackOpen] = useState(false);
    const [aumSnackMessage, setAumSnackMessage] = useState("");

    const [
        optimalPortfolioSummary,
        setOptimalPortfolioSummary,
        optimalPortfolioSummaryRef,
    ] = useLatestState('');
    const [optimalPortfolioSummaryLoaded, setOptimalPortfolioSummaryLoaded] = useState(false);

    const [actualPortfolioIsReady, setActualPortfolioIsReady] = useState(null);
    const [actualPortfolioVersion, setActualPortfolioVersion, actualPortfolioVersionRef] = useLatestState(0);
    const [actualPortfolioId, setActualPortfolioId] = useState(null);
    const [watchlistIsReady, setWatchlistIsReady] = useState(null);
    const [watchlistVersion, setWatchlistVersion, watchlistVersionRef] = useLatestState(0);
    const [watchlistActualSyncVersion, setWatchlistActualSyncVersion] = useState(0);
    const [
        posSizingSyncStatus,
        setPosSizingSyncStatus,
        posSizingSyncStatusRef,
    ] = useLatestState(null);
    const [posSizingSyncStatusLoading, setPosSizingSyncStatusLoading] = useState(false);
    const [posSizingFreshnessDetailsOpen, setPosSizingFreshnessDetailsOpen] = useState(false);
    const [posSizingSyncRequiredPrompt, setPosSizingSyncRequiredPrompt] = useState(null);
    const [imputedWatchlistCogniveIds, setImputedWatchlistCogniveIds] = useState([]);
    const [ignoredWatchlistCogniveIds, setIgnoredWatchlistCogniveIds] = useState([]);

    const location = useLocation();
    const navigate = useNavigate();
    const activeView = resolveDashboardActiveView(location.pathname) || 'overview';
    const activeViewRef = useRef(activeView);
    activeViewRef.current = activeView;
    const {
        updateSetupGuideState,
        setupGuideDismissed,
        dismissSetupGuide,
    } = useAppContext();

    const advanceWatchlistVersion = useCallback(() => {
        const nextWatchlistVersion = watchlistVersionRef.current + 1;
        setWatchlistVersion(nextWatchlistVersion);
        return nextWatchlistVersion;
    }, []);
    const advanceWatchlistActualSyncVersion = useCallback(() => {
        setWatchlistActualSyncVersion((version) => version + 1);
    }, []);
    const beginPublicationVersionTransition = useCallback(({ actualSyncApplied, isCurrent }) => (
        createPublicationVersionTransition({
            readVersions: () => ({ watchlistVersion: watchlistVersionRef.current,
                actualPortfolioVersion: actualPortfolioVersionRef.current }),
            commitVersions: (versions) => {
                setWatchlistVersion(versions.watchlistVersion);
                if (actualSyncApplied) {
                    setActualPortfolioVersion(versions.actualPortfolioVersion);
                    advanceWatchlistActualSyncVersion();
                }
            },
            actualSyncApplied,
            isCurrent,
        })
    ), [advanceWatchlistActualSyncVersion, setActualPortfolioVersion, setWatchlistVersion]);

    const {
        pendingPerformanceImport,
        showPerformancePendingBanner,
        dismissedPerformancePendingRunId,
        setDismissedPerformancePendingRunId,
        performancePendingReviewOpen,
        setPerformancePendingReviewOpen,
        performancePendingReviewRun,
        performancePendingReviewApplying,
        performancePendingReviewError,
        handleReviewPerformancePendingImport,
        handleApplyPerformancePendingImport,
        handleIgnorePerformancePendingImport,
    } = usePortfolioImports({
        userUid: user?.uid,
        activeView,
    });
    const notifiedWatchlistOutcomeRef = useRef(null);

    useEffect(() => {
        const notice = watchlistUpdateOutcomeNotice(securityOperationStatus);
        if (!notice || notifiedWatchlistOutcomeRef.current === notice.key) return;
        notifiedWatchlistOutcomeRef.current = notice.key;
        setSnackbarMessage(notice.message);
        setSnackbarSeverity(notice.severity);
        setSnackbarOpen(true);
    }, [securityOperationStatus]);
    const watchlistOperationActive = isSecurityOperationActive(
        securityOperationStatus,
    );
    // The shared status stays visible briefly after completion. Only an exact
    // accepted client commit can end the consistency lock before it disappears.
    const factorUpdateCommitted = Boolean(
        securityOperationStatus?.revisionId
        && Number.isSafeInteger(securityOperationStatus.revisionSeq)
        && securityOperationStatus.revisionSeq > 0
        && watchlistPublicationState?.committed === true
        && watchlistPublicationState.revisionId === securityOperationStatus.revisionId
        && watchlistPublicationState.revisionSeq === securityOperationStatus.revisionSeq
    );
    const watchlistContextUpdatePending = Boolean(
        exclusiveCoreLoadOwnerRef.current
        || watchlistPublicationState?.publishing
        || ['processing', 'publishing_database', 'publishing_client', 'needs_attention']
            .includes(watchlistPublicationState?.lifecycle)
        || (watchlistOperationActive && !factorUpdateCommitted)
    );

    const [watchlistGuideAcknowledged, setWatchlistGuideAcknowledged] = useState(false);
    const [portfolioGuideAcknowledged, setPortfolioGuideAcknowledged] = useState(false);

    // Selected Risk Overview scope inputs (useLatestState): the explicit
    // refresh command reads their latest values right after they are set.
    const [selectedFactorModel, setSelectedFactorModel, selectedFactorModelRef] = useLatestState(
        formatUserFactorModel(userData?.factor_model)
    );
    const [selectedAxiomaModel, setSelectedAxiomaModel, selectedAxiomaModelRef] = useLatestState(
        normalizeAxiomaFactorDataModel(userData?.axioma_model)
    );
    const [selectedBarraModel, setSelectedBarraModel, selectedBarraModelRef] = useLatestState(
        normalizeBarraFactorDataModel(userData?.barra_model)
    );
    const [selectedBetaSource, setSelectedBetaSource, selectedBetaSourceRef] = useLatestState(
        normalizeBetaSource(userData?.beta_source)
    );
    const [betaSourceTransitionLoading, setBetaSourceTransitionLoading] = useState(false);
    const [
        optimizerIdioVolSource,
        setOptimizerIdioVolSource,
        optimizerIdioVolSourceRef,
    ] = useLatestState(
        normalizeIdioVolSource(userData?.idio_vol_source)
    );
    const posSizingIdioVolSource = optimizerIdioVolSource;
    const [factorSnapshotDate, setFactorSnapshotDate, factorSnapshotDateRef] = useLatestState(null);
    const [factorDataModel, setFactorDataModel, factorDataModelRef] = useLatestState(null);
    const [factorDataFreshnessStatus, setFactorDataFreshnessStatus] = useState(null);
    const publishedWatchlistContextStatusRef = useRef(null);
    const [
        acceptedFactorContext,
        setAcceptedFactorContext,
        acceptedFactorContextRef,
    ] = useLatestState(null);
    const [pendingFactorContext, setPendingFactorContext] = useState(null);
    const [factorDataUpdateLoading, setFactorDataUpdateLoading] = useState(false);
    const [factorDataDetailsOpen, setFactorDataDetailsOpen] = useState(false);
    const [dismissedPendingFactorContextKey, setDismissedPendingFactorContextKey] = useState('');
    const [factorDataStatusRefreshSignal, setFactorDataStatusRefreshSignal] = useState(0);
    const [optimalPortfolioDropdownValues, setOptimalPortfolioDropdownValues] = useState([]);
    const rawAvailableBetaSources = userData?.available_beta_sources ?? null;
    const availableBetaSources = useMemo(
        () => getAvailableBetaSources({ available_beta_sources: rawAvailableBetaSources }),
        [rawAvailableBetaSources],
    );
    const requestedFactorDataModel = useMemo(
        () => getEffectiveFactorDataModel(selectedFactorModel, selectedAxiomaModel, selectedBarraModel),
        [selectedAxiomaModel, selectedBarraModel, selectedFactorModel],
    );
    const acceptedFactorContextKey = getFactorContextKey(acceptedFactorContext);
    const pendingFactorContextKey = getFactorContextKey(pendingFactorContext);
    const {
        activeSelectedFactorModel,
        activeRequestedFactorDataModel,
    } = useMemo(
        () => resolveActiveFactorSelection({
            acceptedFactorContext,
            selectedFactorModel,
            selectedAxiomaModel,
            selectedBarraModel,
        }),
        [acceptedFactorContext, selectedAxiomaModel, selectedBarraModel, selectedFactorModel],
    );
    const latestFactorDataVersionKey = acceptedFactorContext?.versionKey
        || (!acceptedFactorContext ? factorDataFreshnessStatus?.version_key || '' : '');
    const matrixIdentityScopeVersion = useMemo(() => (
        [
            acceptedFactorContext?.watchlistIdentityScopeVersion
                || (!acceptedFactorContext ? factorDataFreshnessStatus?.watchlist_identity_scope_version || '' : ''),
        ].filter(Boolean).join('|')
    ), [
        acceptedFactorContext,
        factorDataFreshnessStatus?.watchlist_identity_scope_version,
    ]);
    const effectiveBetaContextKey = useMemo(
        () => (
            normalizeBetaSource(selectedBetaSource) === 'factor'
                ? `factor:${activeSelectedFactorModel}:${activeRequestedFactorDataModel ?? ''}`
                : normalizeBetaSource(selectedBetaSource)
        ),
        [activeRequestedFactorDataModel, activeSelectedFactorModel, selectedBetaSource],
    );
    const bootstrapSelectionsReady = useMemo(() => {
        if (!userData) {
            return false;
        }

        const expectedFactorModel = formatUserFactorModel(userData?.factor_model);
        const expectedAxiomaModel = normalizeAxiomaFactorDataModel(userData?.axioma_model);
        const expectedBarraModel = normalizeBarraFactorDataModel(userData?.barra_model);
        const normalizedUserBetaSource = normalizeBetaSource(userData?.beta_source);
        const expectedBetaSource = isBetaSourceAvailable(availableBetaSources, normalizedUserBetaSource)
            ? normalizedUserBetaSource
            : 'yahoo';
        const expectedIdioVolSource = normalizeIdioVolSource(userData?.idio_vol_source);

        return (
            selectedFactorModel === expectedFactorModel &&
            selectedAxiomaModel === expectedAxiomaModel &&
            selectedBarraModel === expectedBarraModel &&
            normalizeBetaSource(selectedBetaSource) === expectedBetaSource &&
            normalizeIdioVolSource(optimizerIdioVolSource) === expectedIdioVolSource
        );
    }, [
        availableBetaSources,
        optimizerIdioVolSource,
        selectedAxiomaModel,
        selectedBarraModel,
        selectedBetaSource,
        selectedFactorModel,
        userData,
    ]);

    const [actualPortTradeIdeas, setActualPortTradeIdeas] = useState(null);

    const [priceRefreshLoading, setPriceRefreshLoading] = useState(false);
    const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
    const reportPosSizingPreferenceError = useCallback((message) => {
        setSnackbarMessage(message);
        setSnackbarSeverity('warning');
        setSnackbarOpen(true);
    }, []);
    const posSizingSyncPreferences = usePosSizingSyncPreferences(user?.uid, reportPosSizingPreferenceError);
    const posSizingAutoSyncEnabled = posSizingSyncPreferences.enabled;
    const posSizingAutoSyncEnabledRef = useRef(posSizingAutoSyncEnabled);
    posSizingAutoSyncEnabledRef.current = posSizingAutoSyncEnabled;
    const [pendingAutoRefresh, setPendingAutoRefresh] = useState(null);
    const [pendingPosSizingAutoSync, setPendingPosSizingAutoSync] = useState(null);
    const [posSizingTableSaving, setPosSizingTableSaving] = useState(false);
    const [, setPriceRefreshStatus] = useState(null);
    const [coreLoadVisualLoading, setCoreLoadVisualLoading] = useState(false);
    const [
        optimalPortfolioSummaryId,
        setOptimalPortfolioSummaryId,
        optimalPortfolioSummaryIdRef,
    ] = useLatestState(null);
    const [
        posSizingPortfolioId,
        setPosSizingPortfolioId,
        posSizingPortfolioIdRef,
    ] = useLatestState(null);
    const [posSizingVersion, setPosSizingVersion, posSizingVersionRef] = useLatestState(0);
    const [selectedOverviewInvalidationVersion, setSelectedOverviewInvalidationVersion] = useState(0);
    const lastSeenOptimizedOverviewRefreshRef = useRef({ id: null, token: null });
    const selectedOverviewRefreshRequestIdRef = useRef(0);
    const coreSelectedOverviewOwnerRef = useRef(null);
    const selectedOverviewInvalidationVersionRef = useRef(0);
    const optimizedRiskRequestIdRef = useRef(0);
    const liveQuotesRequestIdRef = useRef(0);
    const holdingsRequestIdRef = useRef(0);
    const matrixLoadRequestIdRef = useRef(0);
    const coreLoadRequestIdRef = useRef(0);
    const coreLoadVisualTokenRef = useRef(0);
    const priceRefreshStatusRequestIdRef = useRef(0);
    const posSizingSyncStatusRequestIdRef = useRef(0);
    const priceRefreshInFlightRef = useRef(false);
    const loadCoreDataRef = useRef(null);
    const riskCommandsRef = useRef(null);
    const refreshPosSizingStatusAfterPublicationRef = useRef(null);
    const requestPosSizingRecheckAfterSaveRef = useRef(null);
    const followPublishedFactorSettingRef = useRef(null);
    const coreLoadInFlightRef = useRef(false);
    const autoRefreshEnabledRef = useRef(true);
    const priceRefreshQueuedRef = useRef(false);
    const queuedPriceRefreshOptionsRef = useRef(null);
    const refreshPriceDataRef = useRef(null);
    const matrixContextKeyRef = useRef('');
    const betaSourceRefreshReadyRef = useRef(false);
    const lastEffectiveBetaContextKeyRef = useRef(null);
    const lastBetaRefreshDriverRef = useRef({
        betaSource: normalizeBetaSource(userData?.beta_source),
        factorKey: '',
    });
    const factorUpdateAcceptanceRef = useRef(null);
    const blockedAutomaticFactorSettingsContextKeyRef = useRef('');
    const betaContextRefreshRunIdRef = useRef(0);
    const publishedBetaContextRef = useRef(null);
    const lastAppliedPriceWatermarkRef = useRef(null);
    const lastPendingPriceWatermarkRef = useRef(null);
    const previousActiveViewRef = useRef(null);
    const posSizingEntrySequenceRef = useRef(0);
    const posSizingFreshnessPromptStateRef = useRef({
        entrySequence: 0,
        actionable: false,
    });
    const lastImmediatePriceProbeRef = useRef(null);
    const lastCoreLoadKeyRef = useRef('');
    const publicationCoreLoadCommitVersionRef = useRef(0);
    const importApplyInFlightRef = useRef(false);
    const forceNextMatricesReloadRef = useRef(false);
    const posSizingRequestIdRef = useRef(0);
    const posSizingPublicationBaseRef = useRef(null);
    const posSizingSyncInFlightRef = useRef(false);
    const posSizingEditingRef = useRef(false);
    const posSizingSavingRef = useRef(false);
    const posSizingPendingChangesRef = useRef(false);
    const persistPosSizingPortfolioRef = useRef(null);
    const groupRiskRequestIdRef = useRef({
        actual: 0,
        opt: 0,
        pos: 0,
    });
    const startupSecondaryLoadsReady = (
        Boolean(lastCoreLoadKeyRef.current) && !coreLoadVisualLoading
    );
    const refreshAuthoritativePosSizingStatus = useCallback(() => {
        setPosSizingSyncStatus(null);
        return refreshPosSizingStatusAfterPublicationRef.current({
            forceFresh: true,
        });
    }, [setPosSizingSyncStatus]);
    const onPosSizingSaved = useCallback(({
        waitForAuthoritativeStatus = true,
        attributeWorkspace = null,
        skipStatusRefresh = false,
        replacesWorkspace = false,
        stagePublicationState = null,
        preparedTarget = null,
        publicationBase = null,
        preparedVersion = null,
    } = {}) => {
        if (stagePublicationState) {
            if (typeof preparedTarget?.reservePosSizingVersion !== 'function') {
                throw new Error('Prepared child save requires an owned version reservation.');
            }
            const version = preparedTarget.reservePosSizingVersion(publicationBase);
            stagePublicationState(() => {
                preparedTarget.acceptPosSizingVersion(version);
                onPosSizingSaved({ waitForAuthoritativeStatus, attributeWorkspace,
                    skipStatusRefresh: true, replacesWorkspace, preparedVersion: version });
            }, preparedTarget.canCommit);
            return null;
        }
        setPosSizingVersion(preparedVersion == null ? (v) => v + 1 : preparedVersion);
        if (replacesWorkspace) setPosSizingAttributeWorkspaceHydration(invalidateLoadedWorkspaceFactorSlice);
        if (
            !replacesWorkspace
            && attributeWorkspace?.state
            && attributeWorkspace?.calculation
            && attributeWorkspace?.publicationBase?.portfolio_row_version != null
        ) {
            setPosSizingAttributeWorkspaceHydration((current) => (
                current?.capability
                    ? {
                        key: `publication:${attributeWorkspace.publicationBase.portfolio_row_version}`,
                        factorSliceUserUid: current.factorSliceUserUid,
                        factorSliceScopeKey: current.factorSliceScopeKey,
                        factorSliceAccepted: current.factorSliceAccepted,
                        factorSlice: current.factorSlice,
                        capability: current.capability,
                        state: attributeWorkspace.state,
                        calculation: attributeWorkspace.calculation,
                    }
                    : current
            ));
        }
        if (skipStatusRefresh) return null;
        // A table save must finish before queued optional context work starts.
        // Signal the coordinator without making that save wait on its own fence.
        void requestPosSizingRecheckAfterSaveRef.current?.({ reason: 'draft_edit_saved' });
        if (!waitForAuthoritativeStatus) {
            const statusRefresh = refreshPosSizingStatusAfterPublicationRef.current({
                forceFresh: true,
            });
            void statusRefresh;
            return null;
        }
        return refreshAuthoritativePosSizingStatus();
    }, [refreshAuthoritativePosSizingStatus, setPosSizingVersion]);

    const invalidateSelectedOverview = useCallback(() => {
        optimizedRiskRequestIdRef.current += 1;
        groupRiskRequestIdRef.current.opt += 1;
        groupRiskRequestIdRef.current.pos += 1;
        selectedOverviewRefreshRequestIdRef.current += 1;
        selectedOverviewInvalidationVersionRef.current += 1;
        setSelectedOverviewInvalidationVersion(
            selectedOverviewInvalidationVersionRef.current,
        );
        setOptimalPortfolioSummaryLoaded(false);
        setOptimizedGroupRiskMetricsLoading(false);
    }, []);

    const beginCoreLoadVisual = useCallback(() => {
        const nextToken = coreLoadVisualTokenRef.current + 1;
        coreLoadVisualTokenRef.current = nextToken;
        setCoreLoadVisualLoading(true);
        return nextToken;
    }, []);

    const endCoreLoadVisual = useCallback((token) => {
        if (coreLoadVisualTokenRef.current === token) {
            setCoreLoadVisualLoading(false);
        }
    }, []);

    autoRefreshEnabledRef.current = autoRefreshEnabled;

    useEffect(() => {
        if (acceptedFactorContextKey || factorDataUpdateLoading) {
            return;
        }
        setFactorSnapshotDate(null);
        setFactorDataModel(null);
        setFactorNames([]);
        setImputedWatchlistCogniveIds([]);
        setIgnoredWatchlistCogniveIds([]);
    }, [
        acceptedFactorContextKey,
        activeSelectedFactorModel,
        activeRequestedFactorDataModel,
        factorDataUpdateLoading,
        setFactorDataModel,
        setFactorSnapshotDate,
    ]);

    useEffect(() => {
        setSelectedBetaSource(normalizeBetaSource(userData?.beta_source));
    }, [setSelectedBetaSource, userData?.beta_source]);

    useEffect(() => {
        setSelectedFactorModel(formatUserFactorModel(userData?.factor_model));
    }, [setSelectedFactorModel, userData?.factor_model]);

    useEffect(() => {
        setSelectedAxiomaModel(normalizeAxiomaFactorDataModel(userData?.axioma_model));
    }, [setSelectedAxiomaModel, userData?.axioma_model]);

    useEffect(() => {
        setSelectedBarraModel(normalizeBarraFactorDataModel(userData?.barra_model));
    }, [setSelectedBarraModel, userData?.barra_model]);

    useEffect(() => {
        setOptimizerIdioVolSource(normalizeIdioVolSource(userData?.idio_vol_source));
    }, [setOptimizerIdioVolSource, userData?.idio_vol_source]);

    useEffect(() => {
        if (
            selectedBetaSource !== 'yahoo' &&
            !isBetaSourceAvailable(availableBetaSources, selectedBetaSource)
        ) {
            setSelectedBetaSource('yahoo');
        }
    }, [availableBetaSources, selectedBetaSource, setSelectedBetaSource]);

    const clearFactorMatrixState = useCallback(() => {
        setFactorSnapshotDate(null);
        setFactorDataModel(null);
        setFactorNames([]);
        setImputedWatchlistCogniveIds([]);
        setIgnoredWatchlistCogniveIds([]);
    }, [setFactorDataModel, setFactorSnapshotDate]);

    const clearRiskDerivedState = useCallback(() => {
        setCalculations(null);
        setHoldings([]);
        setGroupRiskMetrics(null);
        setOptimizedRiskMetrics({});
        setOptimizedGroupRiskMetrics(null);
        setActualPortTradeIdeas([]);
        setPosSizingData([]);
        setPosSizingLoaded(false);
        setPosSizingAttributeWorkspaceHydration(null);
        setWatchlistAttributeSnapshot(null);
    }, []);

    useEffect(() => {
        clearFactorMatrixState();
        clearRiskDerivedState();
        setPositions([]);
        setUserPositions([]);
        setIndustries([]);
        setWatchlistPortfolioEditor(null);
        setPortfolioEditorDialogOpen(false);
        setActualPortfolioId(null);
        setActualPortfolioIsReady(null);
        setWatchlistIsReady(null);
        setPosSizingSyncStatus(null);
        setPosSizingSyncStatusLoading(false);
        setAutoRefreshEnabled(true);
        setPendingAutoRefresh(null);
        setPendingPosSizingAutoSync(null);
        setPriceRefreshStatus(null);
        setFactorDataFreshnessStatus(null);
        setAcceptedFactorContext(null);
        setPendingFactorContext(null);
        setFactorDataUpdateLoading(false);
        setFactorDataDetailsOpen(false);
        setDismissedPendingFactorContextKey('');
        setFactorDataStatusRefreshSignal(0);
        setCoreLoadVisualLoading(false);
        setPosSizingFreshnessDetailsOpen(false);
        setPosSizingSyncRequiredPrompt(null);
        setPortfolioGuideAcknowledged(false);
        setLiveQuotesByCogniveId({});
        setLiveQuotesLastUpdatedAt(null);
        setOverviewSnapshotAt(null);
        setAum(null);
        setGmv(null);
        setError(null);
        setAumLoaded(false);
        setMaxPosSizeNormalVolLoaded(false);
        lastCoreLoadKeyRef.current = '';
        coreLoadInFlightRef.current = false;
        liveQuotesRequestIdRef.current = 0;
        matrixContextKeyRef.current = '';
        coreLoadVisualTokenRef.current = 0;
        betaSourceRefreshReadyRef.current = false;
        lastEffectiveBetaContextKeyRef.current = null;
        lastBetaRefreshDriverRef.current = {
            betaSource: normalizeBetaSource(userData?.beta_source),
            factorKey: '',
        };
        factorUpdateAcceptanceRef.current = null;
        blockedAutomaticFactorSettingsContextKeyRef.current = '';
        betaContextRefreshRunIdRef.current = 0;
        publishedBetaContextRef.current = null;
        lastAppliedPriceWatermarkRef.current = null;
        lastPendingPriceWatermarkRef.current = null;
        previousActiveViewRef.current = activeView;
        posSizingEntrySequenceRef.current = activeView === 'pos-sizing' ? 1 : 0;
        posSizingFreshnessPromptStateRef.current = {
            entrySequence: 0,
            actionable: false,
        };
        selectedOverviewInvalidationVersionRef.current = 0;
        setSelectedOverviewInvalidationVersion(0);
        lastSeenOptimizedOverviewRefreshRef.current = {
            id: null,
            token: null,
            contextKey: '',
            inFlightKey: '',
        };
        // The Risk Overview selection names one of the previous user's portfolios.
        setOptimalPortfolioSummary(null);
        setOptimalPortfolioSummaryId(null);
        setOptimalPortfolioSummaryLoaded(true);
        lastImmediatePriceProbeRef.current = null;
        priceRefreshQueuedRef.current = false;
        queuedPriceRefreshOptionsRef.current = null;
        posSizingSyncInFlightRef.current = false;
        importApplyInFlightRef.current = false;
        setPosSizingDraftSyncLoading(false);
        posSizingEditingRef.current = false;
        posSizingSavingRef.current = false;
        posSizingPendingChangesRef.current = false;
        setPosSizingTableSaving(false);
        updateSetupGuideState({
            step: null,
            targetTab: null,
            hasWatchlist: false,
            hasPortfolio: false,
            hasPosSizing: false,
            watchlistUploadInteracted: false,
            portfolioImportInteracted: false,
        });
    }, [
        user?.uid,
        clearFactorMatrixState,
        clearRiskDerivedState,
        setAcceptedFactorContext,
        setOptimalPortfolioSummary,
        setOptimalPortfolioSummaryId,
        setPosSizingSyncStatus,
        updateSetupGuideState,
    ]);

    useEffect(() => {
        const previousView = previousActiveViewRef.current;
        if (activeView === 'pos-sizing' && previousView !== 'pos-sizing') {
            posSizingEntrySequenceRef.current += 1;
        }
        previousActiveViewRef.current = activeView;
        // Opening Risk Overview is a route change, not a handler call: load
        // what changed while the selected Overview was hidden.
        if (activeView === 'overview' && previousView !== 'overview') {
            getRiskCommandsOrThrow(
                riskCommandsRef,
                'Dashboard.overviewOpened',
                ['refreshSelectedOverview'],
            ).refreshSelectedOverview({ reason: 'overview_opened' });
        }
    }, [activeView]);

    // New Sizing Targets categories start from the member's starting values.
    // Until they load these are undefined and no request carrying them runs.
    const posSizingStartingValues = usePosSizingStartingValues({
        userUid: user?.uid,
        activeView,
        setMaxPosSize,
        setNormalVol,
        setAum,
    });
    const posSizingStartingValuesReady = posSizingStartingValues.startingValues != null;
    const defaultTargetVolPct = posSizingStartingValues.startingValues?.current.target_vol_pct;
    const defaultTargetVolDollar = posSizingStartingValues.startingValues?.current.target_vol_dollar;
    const defaultScaler = posSizingStartingValues.startingValues?.current.scaler;
    const posSizingSettingsCommandRef = useRef(null);
    const combinedHoldings = useCombinedHoldings({
        holdings,
        positions,
        actualPortTradeIdeas,
    });

    // A saved-portfolio list refresh is a selected Overview trigger only when
    // the selected portfolio appeared, disappeared, was re-saved or changed
    // compatibility; workflows that refresh the list also refresh it themselves.
    const handleWhatIfPortfoliosCommitted = useCallback((previousPortfolios, nextPortfolios) => {
        if (!selectedOverviewPortfolioChanged({
            previousPortfolios,
            nextPortfolios,
            selectedId: optimalPortfolioSummaryIdRef.current,
            selectedName: optimalPortfolioSummaryRef.current,
        })) {
            return;
        }
        getRiskCommandsOrThrow(
            riskCommandsRef,
            'Dashboard.handleWhatIfPortfoliosCommitted',
            ['refreshSelectedOverview'],
        ).refreshSelectedOverview({ reason: 'saved_portfolio_changed' });
    }, [optimalPortfolioSummaryIdRef, optimalPortfolioSummaryRef]);

    const {
        setConstraints,
        riskMetricsActPort,
        riskMetricsOptPort,
        riskMetricsDeltaPort,
        displayedExpectedReturnPolicy,
        errorOptimization,
        optimizerQueueState,
        cancelQueuedOptimization,
        setErrorOptimization,
        optimizerIsRunning,
        optimizerRunBlocker,
        runOptimization,
        metaOptimizer,
        constraints,
        gmvBudget,
        groupConstraints,
        setGroupConstraints,
        optimizerV2Capability,
        optimizerAttributeCatalog,
        prepareOptimizerPublication,
        handleWhatIfPortfolioChange,
        optimalPortfolioOptimizer,
        setOptimalPortfolioOptimizer,
        listOfWhatIfPortfolios,
        readWhatIfPortfolios,
        saveOptimizationAndWhatIfPortfolioData,
        fetchOptimizerPortfolios,
        constraintToggles,
        setConstraintToggles,
        showPortfolioMetrics,
        tickerData,
        setTickerData,
        objective,
        setObjective,
        setShowPortfolioMetrics,

        clearOptimizedOverlay,
        unsavedOptimizerRun,
        receiptDisplayActive,
        loadedOptimizerFactorContext,
        loadedOptimizerFactorResult,
        resultStale,
        detachOptimizerResult,
        resetOptimizerLimits,
        optStatus,
        viewMode,
        setViewMode,
        risksByFactor,
        constraintEvaluation,
        resultIssuedAt,
        solverStatus,
        handleAddConstraint,
        handleRemoveConstraint,
        viewModeLoading,
        finalTickerDataFlag,
        resetOptimizerDefaults,
        refreshSavedOptimizedPortfolio,
    } = useOptimizer(
        activeView,
        combinedHoldings,
        posSizingData,
        activeSelectedFactorModel,
        selectedBetaSource,
        factorSnapshotDate,
        factorDataModel,
        optimizerIdioVolSource,
        industries,
        aum,
        selectedAxiomaModel,
        selectedBarraModel,
        user?.uid,
        posSizingSyncStatus?.optimizer_context ?? null,
        refreshAuthoritativePosSizingStatus,
        watchlistVersion,
        watchlistIsReady,
        handleWhatIfPortfoliosCommitted,
        loadedFactorScopeKey,
        exclusiveCoreLoadOwnerRef,
    );

    const selectedSummaryPortfolioMeta = useMemo(() => findSelectedOverviewPortfolio({
        portfolios: listOfWhatIfPortfolios,
        selectedId: optimalPortfolioSummaryId,
        selectedName: optimalPortfolioSummary,
    }), [listOfWhatIfPortfolios, optimalPortfolioSummary, optimalPortfolioSummaryId]);

    const handleOptimizerRestorationError = useCallback((message) => {
        setSnackbarMessage(message || 'The original Watchlist inputs could not be loaded.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
    }, []);

    const handleOverviewOptimizerRestorationRequest = useCallback((portfolio) => {
        if (!optimizerWatchlistSelectionNeedsResolution(portfolio)) {
            return false;
        }
        clearOptimizedOverlay();
        setOptimalPortfolioSummary(null);
        setOptimalPortfolioSummaryId(null);
        setOptimizedRiskMetrics(null);
        setOptimizedGroupRiskMetrics(null);
        setOptimalPortfolioSummaryLoaded(true);
        setError(null);
        if (optimizerWatchlistRestorationRequired(portfolio)) {
            setOverviewOptimizerRestorationPortfolio(portfolio);
        } else {
            setOverviewOptimizerRestorationPortfolio(null);
            handleOptimizerRestorationError(
                'This saved optimized portfolio includes securities that are no longer '
                + 'in your Watchlist, but their original identifiers are unavailable. '
                + 'Add those securities to your Watchlist manually before loading it.',
            );
        }
        return true;
    }, [
        clearOptimizedOverlay,
        handleOptimizerRestorationError,
        setOptimalPortfolioSummary,
        setOptimalPortfolioSummaryId,
    ]);

    const handleOptimizerWatchlistRestorationDeleted = useCallback(({
        portfolioName,
        refreshRequired,
    } = {}) => {
        setOverviewOptimizerRestorationPortfolio(null);
        if (refreshRequired) return;
        setSnackbarMessage(`Portfolio "${portfolioName || 'Saved portfolio'}" deleted.`);
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
    }, []);

    const isPosSizingPortfolioSelection = useCallback((selection = {}) => (
        isPosSizingOverviewSelection({ ...selection, posSizingPortfolioId })
    ), [posSizingPortfolioId]);

    const normalizeContextFactorDataModel = useCallback((factorModel, factorDataModelValue) => {
        const normalizedFactorModel = normalizeUserFactorModel(factorModel);
        return normalizeFactorDataModel(normalizedFactorModel, factorDataModelValue);
    }, []);

    const overviewRiskContextInputs = useMemo(() => buildOverviewRiskContextInputs({
        acceptedFactorContext,
        selectedFactorModel,
        selectedAxiomaModel,
        selectedBarraModel,
        factorDataModel,
        factorSnapshotDate,
        betaSource: selectedBetaSource,
        idioVolSource: optimizerIdioVolSource,
        posSizingAutoSyncEnabled,
        posSizingSyncStatus,
    }), [
        acceptedFactorContext,
        factorDataModel,
        factorSnapshotDate,
        optimizerIdioVolSource,
        posSizingAutoSyncEnabled,
        posSizingSyncStatus,
        selectedAxiomaModel,
        selectedBarraModel,
        selectedBetaSource,
        selectedFactorModel,
    ]);
    const getPortfolioOverviewRiskContext = useCallback((portfolioMeta = null) => (
        buildPortfolioOverviewRiskContext(portfolioMeta, overviewRiskContextInputs)
    ), [overviewRiskContextInputs]);

    // The selected Overview scope from the latest values set, which the
    // refresh command may read before React renders them. It resolves through
    // the same functions as expectedSelectedRiskOverviewScopeKey below.
    const readSelectedOverviewInputs = useCallback(() => ({
        userUid: user?.uid,
        portfolios: readWhatIfPortfolios(),
        selectedId: optimalPortfolioSummaryIdRef.current,
        selectedName: optimalPortfolioSummaryRef.current,
        posSizingPortfolioId: posSizingPortfolioIdRef.current,
        watchlistVersion: watchlistVersionRef.current,
        posSizingVersion: posSizingVersionRef.current,
        invalidationVersion: selectedOverviewInvalidationVersionRef.current,
        riskContextInputs: buildOverviewRiskContextInputs({
            acceptedFactorContext: acceptedFactorContextRef.current,
            selectedFactorModel: selectedFactorModelRef.current,
            selectedAxiomaModel: selectedAxiomaModelRef.current,
            selectedBarraModel: selectedBarraModelRef.current,
            factorDataModel: factorDataModelRef.current,
            factorSnapshotDate: factorSnapshotDateRef.current,
            betaSource: selectedBetaSourceRef.current,
            idioVolSource: optimizerIdioVolSourceRef.current,
            posSizingAutoSyncEnabled: posSizingAutoSyncEnabledRef.current,
            posSizingSyncStatus: posSizingSyncStatusRef.current,
        }),
    }), [
        acceptedFactorContextRef,
        factorDataModelRef,
        factorSnapshotDateRef,
        optimalPortfolioSummaryIdRef,
        optimalPortfolioSummaryRef,
        optimizerIdioVolSourceRef,
        posSizingAutoSyncEnabledRef,
        posSizingPortfolioIdRef,
        posSizingSyncStatusRef,
        posSizingVersionRef,
        readWhatIfPortfolios,
        selectedAxiomaModelRef,
        selectedBarraModelRef,
        selectedBetaSourceRef,
        selectedFactorModelRef,
        user?.uid,
    ]);
    const {
        handleWhatIfPortfolioChangeSummary,
        refreshSelectedOverview,
    } = useOverviewSelectionRefresh({
        activeView,
        exclusiveCoreLoadOwnerRef,
        userUid: user?.uid,
        selectedSummaryPortfolioMeta,
        readSelectedOverviewInputs,
        setOptimalPortfolioSummary,
        setOptimalPortfolioSummaryId,
        setOptimalPortfolioSummaryLoaded,
        setOptimizedRiskMetrics,
        setOptimizedGroupRiskMetrics,
        riskCommandsRef,
        lastSeenOptimizedOverviewRefreshRef,
        selectedOverviewRefreshRequestIdRef,
        coreSelectedOverviewOwnerRef,
        posSizingSyncInFlightRef,
        importApplyInFlightRef,
        onIncompatiblePortfolioSelected: handleOverviewOptimizerRestorationRequest,
    });

    const actualRiskOverviewScope = useMemo(() => buildActualRiskOverviewScope({
        userUid: user?.uid,
        portfolioId: actualPortfolioId,
        actualPortfolioVersion,
        watchlistVersion,
        factorModel: activeSelectedFactorModel,
        factorDataModel: factorDataModel ?? activeRequestedFactorDataModel,
        factorSnapshotDate,
        betaSource: selectedBetaSource,
        idioVolSource: optimizerIdioVolSource,
    }), [
        activeRequestedFactorDataModel,
        activeSelectedFactorModel,
        actualPortfolioId,
        actualPortfolioVersion,
        factorDataModel,
        factorSnapshotDate,
        normalizeContextFactorDataModel,
        optimizerIdioVolSource,
        selectedBetaSource,
        user?.uid,
        watchlistVersion,
    ]);
    const buildActualRiskOverviewScopeKey = useCallback((idioVolSource) => {
        if (!actualRiskOverviewScope.userUid || actualRiskOverviewScope.portfolioId == null) {
            return null;
        }
        return buildRiskOverviewScopeKey({
            ...actualRiskOverviewScope,
            idioVolSource: normalizeIdioVolSource(idioVolSource),
        });
    }, [actualRiskOverviewScope]);
    const actualRiskOverviewScopeKey = useMemo(
        () => buildActualRiskOverviewScopeKey(optimizerIdioVolSource),
        [buildActualRiskOverviewScopeKey, optimizerIdioVolSource],
    );
    // The scope the visible selected Overview panels must match.
    const expectedSelectedRiskOverviewScopeKey = useMemo(() => resolveSelectedOverviewScope({
        userUid: user?.uid,
        portfolios: listOfWhatIfPortfolios,
        selectedId: optimalPortfolioSummaryId,
        selectedName: optimalPortfolioSummary,
        posSizingPortfolioId,
        watchlistVersion,
        posSizingVersion,
        invalidationVersion: selectedOverviewInvalidationVersion,
        riskContextInputs: overviewRiskContextInputs,
    })?.scopeKey ?? null, [
        listOfWhatIfPortfolios,
        optimalPortfolioSummary,
        optimalPortfolioSummaryId,
        overviewRiskContextInputs,
        posSizingPortfolioId,
        posSizingVersion,
        selectedOverviewInvalidationVersion,
        user?.uid,
        watchlistVersion,
    ]);

    const trackedQuoteIdentities = useMemo(() => buildTrackedQuoteIdentities({
        activeView,
        watchlistRows: industries,
        actualHoldings: combinedHoldings,
        selectedHoldings: optimizedRiskMetrics?.holdingsCalculations ?? [],
    }), [activeView, combinedHoldings, industries, optimizedRiskMetrics?.holdingsCalculations]);

    const {
        fetchFactorMatrices,
        buildFactorContextFromResponse,
        getCurrentFactorContext,
        outboundResolutionDialogOpen,
        outboundResolutionIdentityResolution,
        closeOutboundResolutionDialog,
        retryMatricesAfterOutboundChoice,
        factorContextUnavailable,
        factorDataRecoveryRequired,
        factorMatricesUnavailable,
    } = useFactorMatrixWorkflow({
        userUid: user?.uid,
        selectedFactorModel: activeSelectedFactorModel,
        selectedBetaSource,
        requestedFactorDataModel: activeRequestedFactorDataModel,
        watchlistIsReady,
        factorSnapshotDate,
        factorDataModel,
        optimizerIdioVolSource,
        matrixLoadRequestIdRef,
        setMatricesLoading,
        setFactorSnapshotDate,
        setFactorDataModel,
        setFactorNames,
        setImputedWatchlistCogniveIds,
        setIgnoredWatchlistCogniveIds,
        clearFactorMatrixState,
        clearRiskDerivedState,
        setError,
        setSnackbarMessage,
        setSnackbarSeverity,
        setSnackbarOpen,
    });

    const {
        fetchFactorPositions,
        fetchHoldings,
        fetchGroupRiskMetrics,
        refreshActualRiskLensForIdioSource,
        fetchOptimizedRiskMetrics,
    } = useRiskDataFetchers({
        enableHoldingsCalculation: ENABLE_HOLDINGS_CALCULATION,
        userUid: user?.uid,
        selectedFactorModel: activeSelectedFactorModel,
        selectedBetaSource,
        factorSnapshotDate,
        factorDataModel,
        currentIdioVolSource: optimizerIdioVolSource,
        actualPortfolioId,
        holdingsRequestIdRef,
        groupRiskRequestIdRef,
        optimizedRiskRequestIdRef,
        setCalculations,
        setHoldings,
        setGmv,
        setError,
        setSnackbarMessage,
        setSnackbarSeverity,
        setSnackbarOpen,
        setOptimizedGroupRiskMetricsLoading,
        setGroupRisksLoading,
        setOptimizedGroupRiskMetrics,
        setGroupRiskMetrics,
        setOptimizedRiskMetrics,
        setHoldingsLoading,
        setCalculationsLoading,
    });

    useEffect(() => {
        if (!holdings.length) {
            setOverviewSnapshotAt(null);
            return;
        }
        setOverviewSnapshotAt(new Date());
    }, [holdings]);

    const { fetchSecurityQuotes: fetchSecurityQuotesForPublication } = useLiveQuotesPolling({
        userUid: user?.uid,
        activeView,
        trackedQuoteIdentities,
        startupReady: startupSecondaryLoadsReady,
        publicationPaused: Boolean(watchlistPublicationState?.publishing),
        initialFetchDebounceMs: 300,
        watchlistQuotePollMs: WATCHLIST_QUOTE_POLL_MS,
        overviewQuotePollMs: OVERVIEW_QUOTE_POLL_MS,
        importApplyInFlightRef,
        liveQuotesRequestIdRef,
        liveQuotesByCogniveIdRef,
        setLiveQuotesByCogniveId,
        setLiveQuotesLastUpdatedAt,
    });

    const {
        newsByIdentity,
        moverThresholdPct: newsMoverThresholdPct,
        lastUpdatedAt: newsLastUpdatedAt,
        readArticleIds: newsReadArticleIds,
        markArticleRead: markNewsArticleRead,
        recencyWindowHours: newsRecencyWindowHours,
    } = useWatchlistNews({
        userUid: user?.uid,
        enabled: Boolean(user?.uid) && activeView === 'watchlist',
        recencyWindowHours: newsRecencyHours,
        refreshKey: watchlistVersion,
    });
    const {
        articlesByIdentity: newsArticlesByIdentity,
        loadingByIdentity: newsArticlesLoadingByIdentity,
        errorByIdentity: newsArticlesErrorByIdentity,
        loadDetails: loadNewsDetails,
    } = useSecurityNewsDetails({
        userUid: user?.uid,
        enabled: Boolean(user?.uid) && activeView === 'watchlist',
    });

    const {
        fetchOptimizerPortfolioSummaries,
        refreshStatus: optimizerSummaryStatus,
    } = useOptimizerPortfolioSummaries({
        userUid: user?.uid,
        exclusiveCoreLoadOwnerRef,
        posSizingVersion,
        watchlistVersion,
        refreshEnabled: activeView !== 'pos-sizing',
        fetchOptimizerPortfolios,
    });

    const {
        fetchActualPortfolioId,
        clearPositionsForEmptyUniverse,
        fetchPositions,
        fetchIndustries,
        refreshIndustryMetadata,
    } = usePortfolioBaseDataFetchers({
        userUid: user?.uid,
        actualPortfolioId,
        enablePositionsFetch: ENABLE_POSITIONS_FETCH,
        enableIndustriesFetch: ENABLE_INDUSTRIES_FETCH,
        selectedFactorModel: activeSelectedFactorModel,
        selectedBetaSource,
        factorSnapshotDate,
        factorDataModel,
        currentIdioVolSource: optimizerIdioVolSource,
        setActualPortfolioId,
        setActualPortfolioIsReady,
        setUserPositions,
        setPositions,
        setPositionsLoading,
        setIndustries,
        setWatchlistAttributeSnapshot,
        setWatchlistPortfolioEditor,
        setIndustriesLoading,
        setWatchlistIsReady,
        setError,
        setSnackbarMessage,
        setSnackbarSeverity,
        setSnackbarOpen,
    });
    const refreshFactorDetailRiskOverview = useCallback((options) => (
        getRiskCommandsOrThrow(
            riskCommandsRef,
            'Dashboard.refreshFactorDetailRiskOverview',
        ).refreshActualRiskLens(options)
    ), []);
    const {
        selectedFactor,
        rows: sedolsForFactor,
        loading: tickersLoading,
        error: tickersError,
        selectFactor: handleSelectFactor,
        reset: resetFactorPositionDetails,
    } = useFactorPositionDetails({
        exclusiveCoreLoadOwnerRef,
        scopeKey: actualRiskOverviewScopeKey,
        snapshotToken: calculations?.risk_calculation_coverage?.snapshot_token,
        portfolioId: actualPortfolioId,
        riskContext: actualRiskOverviewScope,
        fetchFactorPositions,
        fetchPositions,
        refreshRiskOverview: refreshFactorDetailRiskOverview,
    });
    const clearActualRiskOverviewState = useCallback(() => {
        resetFactorPositionDetails();
        clearPositionsForEmptyUniverse();
        setHoldings([]);
        setCalculations(null);
        setGroupRiskMetrics(null);
        setGmv(null);
        setActualPortTradeIdeas([]);
        setHoldingsLoading(false);
        setCalculationsLoading(false);
        setGroupRisksLoading(false);
    }, [clearPositionsForEmptyUniverse, resetFactorPositionDetails]);

    const handleSavedDraftSyncRequired = useCallback((prompt) => {
        setPosSizingFreshnessDetailsOpen(false);
        setPosSizingSyncRequiredPrompt((current) => {
            const message = String(prompt?.message || '').trim();
            const code = String(prompt?.code || '').trim();
            if (current?.message === message && current?.code === code) {
                return current;
            }
            return { code, message };
        });
    }, []);

    const {
        fetchPosSizingData,
        posSizingLoadError,
        posSizingLoadSyncPrompt,
        posSizingOutboundResolutionDialogOpen,
        posSizingOutboundResolutionIdentityResolution,
        closePosSizingOutboundResolutionDialog,
        retryPosSizingAfterOutboundChoice,
    } = usePosSizingDataFetcher({
        userUid: user?.uid,
        exclusiveCoreLoadOwnerRef,
        factorSliceScopeKey: loadedFactorScopeKey,
        selectedFactorModel: activeSelectedFactorModel,
        factorSnapshotDate,
        factorDataModel,
        posSizingIdioVolSource,
        selectedBetaSource,
        defaultTargetVolPct,
        defaultTargetVolDollar,
        defaultScaler,
        setError,
        setSnackbarMessage,
        setSnackbarSeverity,
        setSnackbarOpen,
        setPosSizingData,
        setPosSizingLoading,
        setPosSizingLoaded,
        posSizingRequestIdRef,
        persistPosSizingPortfolioRef,
        posSizingPublicationBaseRef,
        setPosSizingSyncStatus,
        setPosSizingAttributeWorkspaceHydration,
        onSavedDraftSyncRequired: handleSavedDraftSyncRequired,
        onWatchlistStartAvailable: posSizingStartingValues.markWatchlistStartPromptPending,
    });

    useEffect(() => {
        if (posSizingOutboundResolutionDialogOpen && outboundResolutionDialogOpen) {
            closeOutboundResolutionDialog();
        }
    }, [
        closeOutboundResolutionDialog,
        outboundResolutionDialogOpen,
        posSizingOutboundResolutionDialogOpen,
    ]);

    const outboundResolutionContext = posSizingOutboundResolutionDialogOpen
        ? 'pos_sizing'
        : (outboundResolutionDialogOpen ? 'factor_matrices' : null);
    const activeOutboundResolutionDialogOpen = Boolean(outboundResolutionContext);
    const activeOutboundResolutionIdentityResolution = outboundResolutionContext === 'pos_sizing'
        ? posSizingOutboundResolutionIdentityResolution
        : outboundResolutionIdentityResolution;

    const closeActiveOutboundResolutionDialog = useCallback(() => {
        if (outboundResolutionContext === 'pos_sizing') {
            closePosSizingOutboundResolutionDialog();
            return;
        }
        closeOutboundResolutionDialog();
    }, [
        closeOutboundResolutionDialog,
        closePosSizingOutboundResolutionDialog,
        outboundResolutionContext,
    ]);

    const retryActiveOutboundResolution = useCallback(async () => {
        if (outboundResolutionContext === 'pos_sizing') {
            await retryPosSizingAfterOutboundChoice();
            return;
        }
        await retryMatricesAfterOutboundChoice();
    }, [
        outboundResolutionContext,
        retryMatricesAfterOutboundChoice,
        retryPosSizingAfterOutboundChoice,
    ]);

    const { fetchTradeIdeasFromGmvActuals } = useTradeIdeasWorkflow({
        userUid: user?.uid,
        selectedFactorModel: activeSelectedFactorModel,
        selectedBetaSource,
        factorSnapshotDate,
        factorDataModel,
        idioVolSource: posSizingIdioVolSource,
        defaultTargetVolPct,
        defaultTargetVolDollar,
        defaultScaler,
        setActualPortTradeIdeas,
        setError,
        setSnackbarMessage,
        setSnackbarSeverity,
        setSnackbarOpen,
    });

    const actualTradeIdeasContextKey = useMemo(() => {
        if (!user?.uid || actualPortfolioId == null) {
            return '';
        }
        return JSON.stringify([
            user.uid,
            actualPortfolioId,
            actualPortfolioVersion,
            watchlistVersion,
            activeSelectedFactorModel,
            selectedBetaSource,
            factorSnapshotDate,
            factorDataModel,
            posSizingIdioVolSource,
            latestFactorDataVersionKey,
            matrixIdentityScopeVersion,
            overviewSnapshotAt,
            defaultTargetVolPct,
            defaultTargetVolDollar,
            defaultScaler,
        ]);
    }, [
        activeSelectedFactorModel,
        actualPortfolioId,
        actualPortfolioVersion,
        defaultScaler,
        defaultTargetVolDollar,
        defaultTargetVolPct,
        factorDataModel,
        factorSnapshotDate,
        latestFactorDataVersionKey,
        matrixIdentityScopeVersion,
        overviewSnapshotAt,
        posSizingIdioVolSource,
        selectedBetaSource,
        user?.uid,
        watchlistVersion,
    ]);
    const { loading: actualTradeIdeasLoading } = useOnDemandActualTradeIdeas({
        userUid: user?.uid,
        activeView,
        optimizerViewMode: viewMode,
        startupReady: startupSecondaryLoadsReady,
        enabled: actualPortfolioIsReady === true
            && combinedHoldings.length > 0
            && posSizingStartingValuesReady,
        contextKey: actualTradeIdeasContextKey,
        fetchTradeIdeas: fetchTradeIdeasFromGmvActuals,
        setActualPortTradeIdeas,
    });
    const optimizerBaselineLoading = viewModeLoading || (
        activeView === 'optimizer'
        && viewMode === 'normal'
        && actualTradeIdeasLoading
    );

    const {
        queuePriceRefresh,
        consumeQueuedPriceRefresh,
    } = useQueuedPriceRefresh({
        priceRefreshQueuedRef,
        queuedPriceRefreshOptionsRef,
    });

    const {
        readPriceRefreshStatusRaw,
        loadCoreData,
    } = useCoreLoadOrchestrator({
        userUid: user?.uid,
        onCoreFactorContextCommitted: commitLoadedCoreFactorSlice,
        currentIdioVolSource: optimizerIdioVolSource,
        currentBetaSource: selectedBetaSource,
        watchlistVersion,
        posSizingVersion,
        watchlistIdentityScopeVersion: matrixIdentityScopeVersion,
        latestFactorDataVersionKey,
        actualPortfolioVersion,
        selectedFactorModel: activeSelectedFactorModel,
        requestedFactorDataModel: activeRequestedFactorDataModel,
        factorSnapshotDate,
        optimalPortfolioSummaryId,
        optimalPortfolioSummary,
        selectedSummaryPortfolioMeta,
        listOfWhatIfPortfolios,
        matrixContextKeyRef,
        coreLoadRequestIdRef,
        lastAppliedPriceWatermarkRef,
        lastPendingPriceWatermarkRef,
        lastSeenOptimizedOverviewRefreshRef,
        selectedOverviewRefreshRequestIdRef,
        coreSelectedOverviewOwnerRef,
        exclusiveCoreLoadOwnerRef,
        selectedOverviewInvalidationVersionRef,
        fetchIndustries,
        fetchOptimizerPortfolioSummaries,
        fetchPositions,
        fetchHoldings,
        fetchGroupRiskMetrics,
        fetchFactorMatrices,
        buildFactorContextFromResponse,
        getCurrentFactorContext,
        fetchActualPortfolioId,
        fetchSecurityQuotesForPublication,
        clearActualRiskOverviewState,
        getPortfolioOverviewRiskContext,
        isPosSizingPortfolioSelection,
        fetchOptimizedRiskMetrics,
        setOptimalPortfolioSummaryLoaded,
        setPriceRefreshStatus,
        setPendingAutoRefresh,
    });
    loadCoreDataRef.current = loadCoreData;
    const { fetchMaxPosSizeAndNormalVol } = usePortfolioRiskConfig({
        userUid: user?.uid,
        actualPortfolioVersion,
        startupReady: startupSecondaryLoadsReady,
        activeView,
        onStartingValuesLoaded: posSizingStartingValues.applyStartingValuesResponse,
        maxPosSizeNormalVolLoaded,
        maxPosSizeNormalVolLoading,
        aumLoaded,
        aumLoading,
        riskCommandsRef,
        setMaxPosSize,
        setNormalVol,
        setAum,
        setMaxPosSizeNormalVolLoading,
        setMaxPosSizeNormalVolLoaded,
        setAumLoading,
        setAumLoaded,
        setSnackbarMessage,
        setSnackbarSeverity,
        setSnackbarOpen,
    });
    posSizingStartingValues.loadStartingValuesRef.current = fetchMaxPosSizeAndNormalVol;

    const {
        fetchPriceRefreshStatus,
        notifyPendingPriceRefresh,
        markPendingRefreshFromLoadResult,
    } = usePriceRefreshStatus({
        userUid: user?.uid,
        readPriceRefreshStatusRaw,
        autoRefreshEnabledRef,
        lastAppliedPriceWatermarkRef,
        lastPendingPriceWatermarkRef,
        priceRefreshStatusRequestIdRef,
        setPriceRefreshStatus,
        setPendingAutoRefresh,
        setSnackbarMessage,
        setSnackbarSeverity,
        setSnackbarOpen,
    });

    const {
        buildFreshContextPublicationFields,
        reconcilePosSizingContext,
        startPosSizingFromWatchlist,
        handlePosSizingUploadSuccess,
    } = usePosSizingPersistence({
        onPosSizingSaved,
        maxPosSize,
        normalVol,
        selectedFactorModel: activeSelectedFactorModel,
        selectedBetaSource,
        posSizingIdioVolSource,
        factorSnapshotDate,
        setSnackbarMessage,
        setSnackbarSeverity,
        setSnackbarOpen,
        persistPosSizingPortfolioRef,
        factorDataModel,
        defaultTargetVolPct,
        defaultTargetVolDollar,
        defaultScaler,
        setError,
        setPosSizingData,
        setPosSizingLoading,
        setPosSizingLoaded,
        posSizingRequestIdRef,
        posSizingPublicationBaseRef,
        resolveActualSyncStartingValues: posSizingStartingValues.resolveActualSyncStartingValues,
    });

    const posSizingSyncWatchlistRows = !industriesLoading && watchlistIsReady != null
        ? industries : null;
    const {
        fetchPosSizingSyncStatus,
        requestPosSizingContextUpdate,
        contextUpdateState,
        handlePosSizingEditingChange,
        handlePosSizingSavingChange,
        handlePosSizingPendingChangesChange,
        handlePosSizingFreshnessDetailsOpenChange,
        handleSyncPosSizingDraft,
    } = usePosSizingSync({
        userUid: user?.uid,
        sessionScopeKey: loadedFactorScopeKey,
        activeView,
        actualPortfolioIsReady,
        watchlistRows: posSizingSyncWatchlistRows,
        selectedFactorModel: activeSelectedFactorModel,
        requestedFactorDataModel: activeRequestedFactorDataModel,
        factorSnapshotDate,
        selectedBetaSource,
        posSizingIdioVolSource,
        posSizingLoading,
        posSizingDraftSyncLoading,
        posSizingSyncStatus,
        posSizingAutoSyncEnabled,
        autoSyncPreferencesReady: posSizingSyncPreferences.ready,
        reconcilePosSizingContext,
        posSizingPublicationBaseRef,
        syncStatusBootstrapEnabled: bootstrapSelectionsReady,
        actualPortfolioVersion,
        importApplyInFlightRef,
        exclusiveCoreLoadOwnerRef,
        onActualSyncError: handleSavedDraftSyncRequired,
        riskCommandsRef,
        setPosSizingSyncStatus,
        setPosSizingSyncStatusLoading,
        setPendingPosSizingAutoSync,
        setPosSizingFreshnessDetailsOpen,
        setPosSizingDraftSyncLoading,
        setPosSizingTableSaving,
        posSizingSyncStatusRequestIdRef,
        posSizingAutoSyncEnabledRef,
        posSizingSyncInFlightRef,
        posSizingEditingRef,
        posSizingSavingRef,
        posSizingPendingChangesRef,
    });
    refreshPosSizingStatusAfterPublicationRef.current = fetchPosSizingSyncStatus;
    requestPosSizingRecheckAfterSaveRef.current = requestPosSizingContextUpdate;
    const handleUserSyncPosSizingDraft = useCallback(async (options) => {
        const result = await handleSyncPosSizingDraft({
            ...options,
            // A draft the workspace could not load must be republished.
            requireRebuild: Boolean(posSizingLoadSyncPrompt),
        });
        if (result?.success) {
            setPosSizingSyncRequiredPrompt(null);
        }
        return result;
    }, [handleSyncPosSizingDraft, posSizingLoadSyncPrompt]);
    const handleSyncRequiredPosSizingDraft = useCallback(async () => {
        const authoritativeStatus = await fetchPosSizingSyncStatus({ forceFresh: true });
        if (!authoritativeStatus) {
            return {
                success: false,
                message: 'Unable to verify Position Sizing readiness. Retry Sync Draft.',
            };
        }
        return await handleUserSyncPosSizingDraft({
            syncStatusOverride: authoritativeStatus,
        });
    }, [fetchPosSizingSyncStatus, handleUserSyncPosSizingDraft]);

    const riskCommands = useRiskOrchestrationCommands({
        userUid: user?.uid,
        loadCoreDataRef,
        fetchOptimizedRiskMetrics,
        fetchGroupRiskMetrics,
        refreshActualRiskLensForIdioSource,
        fetchPosSizingData,
        fetchPosSizingSyncStatus,
        fetchOptimizerPortfolioSummaries,
        matrixContextKeyRef,
        invalidateSelectedOverview,
        refreshSelectedOverview,
    });
    riskCommandsRef.current = riskCommands;

    // A first draft from the Watchlist or a file upload publishes a fresh
    // context; reload the workspace exactly as Sync Draft from Actual does
    // after publishing.
    const reloadPosSizingAfterPublication = useCallback(async (reason) => {
        const commands = getRiskCommandsOrThrow(
            riskCommandsRef,
            'Dashboard.reloadPosSizingAfterPublication',
            ['invalidateRiskSurface', 'refreshPosSizingContext', 'refreshSelectedOverview'],
        );
        try {
            commands.invalidateRiskSurface({
                cause: RISK_INVALIDATION_CAUSES.POS_SIZING_RESULT_CHANGED,
                reason,
            });
            const followup = await commands.refreshPosSizingContext({
                reason,
                fetchData: true,
                persistPortfolio: false,
                includeSyncStatus: false,
                refreshOptimizerSummaries: true,
            });
            return followup?.result?.pos_sizing ?? null;
        } finally {
            commands.refreshSelectedOverview({ reason });
        }
    }, []);

    // The first save of a Watchlist-only user creates the draft.
    const handleStartPosSizingFromWatchlist = useCallback(async (request) => {
        const commands = getRiskCommandsOrThrow(
            riskCommandsRef,
            'Dashboard.handleStartPosSizingFromWatchlist',
            ['invalidateRiskSurface', 'refreshPosSizingContext', 'refreshSelectedOverview'],
        );
        const reason = 'pos_sizing_watchlist_start';
        let published;
        try {
            published = await startPosSizingFromWatchlist(request);
        } catch (error) {
            // Re-read the workspace so a still-allowed start gets its no-draft
            // base back and the unsaved rows can be retried without a reload.
            await commands.refreshPosSizingContext({
                reason,
                fetchData: true,
                persistPortfolio: false,
                includeSyncStatus: false,
            });
            throw error;
        }
        let hydratedRows = null;
        try {
            hydratedRows = await reloadPosSizingAfterPublication(reason);
        } catch (error) {
            console.error('Position Sizing was created but could not be reloaded:', error);
        }
        return {
            pos_sizing: hydratedRows ?? published.results?.pos_sizing ?? [],
            added: published.receipt.canonical_count,
        };
    }, [reloadPosSizingAfterPublication, startPosSizingFromWatchlist]);

    // An upload may create the first draft or replace the saved one. A reload
    // failure reaches the uploader, which says the upload itself was saved.
    const handlePosSizingFileUploadSuccess = useCallback(async (results, receipt) => {
        await handlePosSizingUploadSuccess(results, receipt);
        await reloadPosSizingAfterPublication('pos_sizing_file_upload');
    }, [handlePosSizingUploadSuccess, reloadPosSizingAfterPublication]);

    // The Sizing defaults dialog: Max Pos Size and Normal Vol show the
    // draft's current values, the other three the starting values.
    const { saveSizingDefaults, suggestTargetVolDollar } = posSizingStartingValues;
    const handleSaveSizingDefaults = useCallback((values) => saveSizingDefaults(values, {
        currentMaxPosSize: maxPosSize,
        currentNormalVol: normalVol,
        applySettings: async (settings) => (
            await posSizingSettingsCommandRef.current?.(settings) ?? false
        ),
        hasDraft: () => posSizingPublicationBaseRef.current?.portfolio_id != null,
        reloadDraft: () => reloadPosSizingAfterPublication('pos_sizing_starting_values'),
    }), [maxPosSize, normalVol, reloadPosSizingAfterPublication, saveSizingDefaults]);
    const handleSuggestSizingDefaults = useCallback(
        (values) => suggestTargetVolDollar('draft', values),
        [suggestTargetVolDollar],
    );

    const buildConfiguredFactorContext = useCallback((status, reason) => (
        buildFactorContextFromStatus({
            status,
            factorModel: selectedFactorModel,
            factorDataModel: requestedFactorDataModel,
            reason,
            previousContext: acceptedFactorContext,
        })
    ), [
        acceptedFactorContext,
        requestedFactorDataModel,
        selectedFactorModel,
    ]);

    const stagePendingFactorContext = useCallback((context) => {
        const nextKey = getFactorContextKey(context);
        if (!nextKey) {
            return;
        }
        setPendingFactorContext((current) => {
            if (getFactorContextKey(current) === nextKey) {
                return current;
            }
            return context;
        });
        setDismissedPendingFactorContextKey((currentDismissedKey) => (
            currentDismissedKey === nextKey ? currentDismissedKey : ''
        ));
    }, []);

    const handleInitialFactorStatusAccepted = useCallback((status) => {
        setAcceptedFactorContext((current) => {
            if (getFactorContextKey(current)) {
                return current;
            }
            return buildConfiguredFactorContext(status, 'initial_status');
        });
    }, [buildConfiguredFactorContext, setAcceptedFactorContext]);

    const beginFactorContextAcceptance = useCallback((context, { keepPending = true } = {}) => {
        const pendingKey = getFactorContextKey(context);
        if (
            !pendingKey
            || factorDataUpdateLoading
            || exclusiveCoreLoadOwnerRef.current
            || !isFactorContextLoadable(context)
        ) {
            return { success: false };
        }
        factorUpdateAcceptanceRef.current = {
            pendingContext: context,
            previousContext: acceptedFactorContext,
            pendingKey,
            previousKey: acceptedFactorContextKey,
        };
        if (keepPending) {
            setPendingFactorContext(context);
        } else {
            setPendingFactorContext(null);
        }
        setDismissedPendingFactorContextKey(pendingKey);
        setFactorDataDetailsOpen(false);
        setFactorDataUpdateLoading(true);
        setAcceptedFactorContext(context);
        return { success: true };
    }, [
        acceptedFactorContext,
        acceptedFactorContextKey,
        exclusiveCoreLoadOwnerRef,
        factorDataUpdateLoading,
        setAcceptedFactorContext,
    ]);

    const isAutomaticDownstreamFactorContextChange = useCallback((nextContext) => {
        if (!isFactorContextLoadable(nextContext)) {
            return false;
        }
        return factorContextsUseSameFactorGeneration(
            acceptedFactorContext,
            nextContext,
        );
    }, [acceptedFactorContext]);

    const handlePendingFactorContext = useCallback(({ status }) => {
        // Legacy/no-Watchlist startup can still load browser metadata. Published
        // Watchlists advance only through the atomic context update workflow.
        if (publishedWatchlistContextStatusRef.current?.status !== 'no_published_watchlist') return;
        const nextContext = buildConfiguredFactorContext(status, 'inventory_changed');
        if (shouldPromoteVerifiedFactorContext({ acceptedContext: acceptedFactorContext, nextContext })) {
            setAcceptedFactorContext(nextContext);
            setPendingFactorContext((current) => (
                factorContextsUseSameFactorDataSnapshot(current, nextContext) ? null : current
            ));
            setDismissedPendingFactorContextKey('');
            setFactorDataDetailsOpen(false);
            return;
        }
        if (isAutomaticDownstreamFactorContextChange(nextContext)) {
            beginFactorContextAcceptance({
                ...nextContext,
                reason: 'msi_security_resolution_applied',
            }, { keepPending: false });
            return;
        }
        const nextReason = factorContextsUseSameSelection(acceptedFactorContext, nextContext)
            ? 'inventory_changed'
            : 'settings_changed';
        if (nextReason === 'settings_changed' && isFactorContextLoadable(nextContext)) {
            const nextContextKey = getFactorContextKey(nextContext);
            if (
                nextContextKey
                && blockedAutomaticFactorSettingsContextKeyRef.current === nextContextKey
            ) {
                return;
            }
            beginFactorContextAcceptance({
                ...nextContext,
                reason: nextReason,
            }, { keepPending: false });
            return;
        }
        stagePendingFactorContext({
            ...nextContext,
            reason: nextReason,
        });
    }, [
        acceptedFactorContext,
        beginFactorContextAcceptance,
        buildConfiguredFactorContext,
        isAutomaticDownstreamFactorContextChange,
        setAcceptedFactorContext,
        stagePendingFactorContext,
    ]);
    const handlePendingFactorContextCleared = useCallback(({ nextMatrixScopeVersionKey }) => {
        if (!acceptedFactorContextKey || nextMatrixScopeVersionKey !== acceptedFactorContextKey) {
            return;
        }
        setPendingFactorContext((current) => (
            getFactorContextKey(current) ? null : current
        ));
        setDismissedPendingFactorContextKey('');
        setFactorDataDetailsOpen(false);
    }, [acceptedFactorContextKey]);

    useFactorDataFreshnessPolling({
        userUid: user?.uid,
        startupReady: startupSecondaryLoadsReady,
        selectedFactorModel,
        requestedFactorDataModel,
        refreshSignal: factorDataStatusRefreshSignal,
        acceptedFactorContextKey,
        matricesLoading,
        coreLoadInFlightRef,
        exclusiveCoreLoadOwnerRef,
        priceRefreshInFlightRef,
        importApplyInFlightRef,
        onStatus: setFactorDataFreshnessStatus,
        onInitialStatusAccepted: handleInitialFactorStatusAccepted,
        // Available server metadata cannot advance the published app context.
        // useWatchlistContextSync owns prompting and atomic publication.
        onPendingFactorContext: handlePendingFactorContext,
        onPendingFactorContextCleared: handlePendingFactorContextCleared,
    });

    useEffect(() => {
        if (acceptedFactorContextKey) {
            return;
        }
        if (!factorSnapshotDate || !factorDataModel) {
            return;
        }
        const activeContext = {
            factorModel: activeSelectedFactorModel,
            factorDataModel: factorDataModel ?? activeRequestedFactorDataModel,
        };
        const statusMatchesActiveContext = factorContextsUseSameSelection(
            activeContext,
            {
                factorModel: factorDataFreshnessStatus?.factor_model,
                factorDataModel: factorDataFreshnessStatus?.factor_data_model,
            },
        );
        setAcceptedFactorContext(buildFactorContextFromStatus({
            status: statusMatchesActiveContext ? factorDataFreshnessStatus : null,
            factorModel: activeSelectedFactorModel,
            factorDataModel: factorDataModel ?? activeRequestedFactorDataModel,
            factorSnapshotDate,
            reason: 'initial_matrix_load',
        }));
    }, [
        acceptedFactorContextKey,
        activeRequestedFactorDataModel,
        activeSelectedFactorModel,
        factorDataFreshnessStatus,
        factorDataModel,
        factorSnapshotDate,
        setAcceptedFactorContext,
    ]);

    useEffect(() => {
        if (!acceptedFactorContextKey || factorDataUpdateLoading) {
            return;
        }
        const configuredContext = {
            factorModel: selectedFactorModel,
            factorDataModel: requestedFactorDataModel,
        };
        const activeContext = {
            factorModel: activeSelectedFactorModel,
            factorDataModel: activeRequestedFactorDataModel,
        };
        if (factorContextsUseSameSelection(configuredContext, activeContext)) {
            blockedAutomaticFactorSettingsContextKeyRef.current = '';
            setPendingFactorContext((current) => (
                current?.reason === 'settings_changed' ? null : current
            ));
            return;
        }
        // Without a published Watchlist, a deliberate settings change is
        // loaded directly when freshness returns its verified target. Preserve
        // an already-failed target as a quiet, explicit retry; do not
        // manufacture or repeatedly auto-run a provisional settings context
        // from polling alone. A published Watchlist already uses the saved
        // setting, so it is followed below.
        setPendingFactorContext((current) => {
            if (current?.reason !== 'settings_changed') {
                return current;
            }
            return factorContextsUseSameSelection(current, configuredContext)
                ? current
                : null;
        });
        if (!blockedAutomaticFactorSettingsContextKeyRef.current) {
            setDismissedPendingFactorContextKey('');
        }
        setFactorDataDetailsOpen(false);
        if (publishedWatchlistContextStatusRef.current?.status !== 'no_published_watchlist') {
            void followPublishedFactorSettingRef.current?.();
        }
    }, [
        acceptedFactorContext,
        acceptedFactorContextKey,
        activeRequestedFactorDataModel,
        activeSelectedFactorModel,
        factorDataUpdateLoading,
        requestedFactorDataModel,
        selectedFactorModel,
    ]);

    useEffect(() => {
        if (!pendingFactorContextKey) {
            return;
        }
        if (pendingFactorContextKey === dismissedPendingFactorContextKey) {
            return;
        }
        if (activeView !== 'overview') {
            return;
        }
        if (pendingFactorContext?.reason === 'settings_changed') {
            return;
        }
        setFactorDataDetailsOpen(true);
    }, [
        activeView,
        dismissedPendingFactorContextKey,
        pendingFactorContext?.reason,
        pendingFactorContextKey,
    ]);

    const handleFactorDataDetailsOpenChange = useCallback((open) => {
        if (open) {
            setFactorDataDetailsOpen(true);
            return;
        }
        if (pendingFactorContextKey) {
            setDismissedPendingFactorContextKey(pendingFactorContextKey);
        }
        setFactorDataDetailsOpen(false);
    }, [pendingFactorContextKey]);

    const handleLoadPendingFactorData = useCallback(() => (
        beginFactorContextAcceptance(pendingFactorContext)
    ), [
        beginFactorContextAcceptance,
        pendingFactorContext,
    ]);

    useEffect(() => {
        const request = factorUpdateAcceptanceRef.current;
        if (!factorDataUpdateLoading || !request) {
            return undefined;
        }
        if (acceptedFactorContextKey !== request.pendingKey) {
            return undefined;
        }

        const commands = getRiskCommandsOrThrow(
            riskCommandsRef,
            'Dashboard.handleLoadPendingFactorData',
            ['refreshRiskSurface', 'refreshPosSizingContext', 'refreshSelectedOverview'],
        );
        const refreshReason = request.pendingContext?.reason === 'msi_security_resolution_applied'
            ? 'msi_security_resolution_applied'
            : 'factor_data_refresh';
        let cancelled = false;
        const coreLoadVisualToken = beginCoreLoadVisual();
        coreLoadInFlightRef.current = true;

        void (async () => {
            try {
                const loadResult = await commands.refreshRiskSurface({
                    reason: refreshReason,
                    requireFreshMatrices: true,
                    preserveExistingOnMatrixFailure: true,
                    latestFactorDataVersionKey: request.pendingContext?.versionKey || undefined,
                    watchlistIdentityScopeVersion:
                        request.pendingContext?.watchlistIdentityScopeVersion || undefined,
                    factorDataModelOverride:
                        request.pendingContext?.factorDataModel || undefined,
                    factorSnapshotDateOverride:
                        request.pendingContext?.factorSnapshotDate || undefined,
                    includeSelectedOverview: true,
                    includeOptimizerSummaries: true,
                });
                if (cancelled) {
                    return;
                }
                if (!isReadyFactorMatrixRefreshResult(loadResult)) {
                    const isDirectSettingsChange = (
                        request.pendingContext?.reason === 'settings_changed'
                    );
                    if (isPendingFactorMatrixRefreshResult(loadResult)) {
                        if (isDirectSettingsChange) {
                            blockedAutomaticFactorSettingsContextKeyRef.current = request.pendingKey;
                        }
                        setAcceptedFactorContext(request.previousContext ?? null);
                        setPendingFactorContext(request.pendingContext ?? null);
                        setDismissedPendingFactorContextKey(request.pendingKey);
                        setFactorDataDetailsOpen(false);
                        setFactorDataStatusRefreshSignal((value) => value + 1);
                        return;
                    }
                    const matrixStatus = loadResult?.matrixStatus || 'unavailable';
                    if (matrixStatus === 'cancelled') {
                        return;
                    }
                    console.warn('Staged factor-data update did not produce ready matrices:', loadResult);
                    if (isDirectSettingsChange) {
                        blockedAutomaticFactorSettingsContextKeyRef.current = request.pendingKey;
                    }
                    setAcceptedFactorContext(request.previousContext ?? null);
                    setPendingFactorContext(request.pendingContext ?? null);
                    setDismissedPendingFactorContextKey(
                        isDirectSettingsChange ? request.pendingKey : '',
                    );
                    setFactorDataDetailsOpen(!isDirectSettingsChange);
                    setSnackbarMessage(`Factor data update is not ready (${matrixStatus}). Keeping the current dashboard context.`);
                    setSnackbarSeverity('warning');
                    setSnackbarOpen(true);
                    return;
                }
                markPendingRefreshFromLoadResult(loadResult);
                blockedAutomaticFactorSettingsContextKeyRef.current = '';
                await requestPosSizingContextUpdate({ reason: refreshReason, allowParentPublication: true });
                if (cancelled) {
                    return;
                }
                setPendingFactorContext((current) => (
                    getFactorContextKey(current) === request.pendingKey ? null : current
                ));
                lastCoreLoadKeyRef.current = `${user?.uid}|${activeSelectedFactorModel}|${activeRequestedFactorDataModel ?? ''}`;
                setFactorDataDetailsOpen(false);
                setSnackbarMessage('Factor data loaded.');
                setSnackbarSeverity('success');
                setSnackbarOpen(true);
            } catch (error) {
                if (!cancelled) {
                    const isDirectSettingsChange = (
                        request.pendingContext?.reason === 'settings_changed'
                    );
                    console.error('Error loading factor data update:', error);
                    if (isDirectSettingsChange) {
                        blockedAutomaticFactorSettingsContextKeyRef.current = request.pendingKey;
                    }
                    setAcceptedFactorContext(request.previousContext ?? null);
                    setPendingFactorContext(request.pendingContext ?? null);
                    setDismissedPendingFactorContextKey(
                        isDirectSettingsChange ? request.pendingKey : '',
                    );
                    setFactorDataDetailsOpen(!isDirectSettingsChange);
                    setSnackbarMessage('Failed to load factor data update.');
                    setSnackbarSeverity('error');
                    setSnackbarOpen(true);
                }
            } finally {
                if (!cancelled) {
                    factorUpdateAcceptanceRef.current = null;
                    coreLoadInFlightRef.current = false;
                    setFactorDataUpdateLoading(false);
                    endCoreLoadVisual(coreLoadVisualToken);
                }
                // Also after a cancelled or rolled-back load: the command only
                // requests a selected Overview that is out of date.
                commands.refreshSelectedOverview({ reason: refreshReason });
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [
        acceptedFactorContextKey,
        activeRequestedFactorDataModel,
        activeSelectedFactorModel,
        beginCoreLoadVisual,
        coreLoadInFlightRef,
        endCoreLoadVisual,
        factorDataUpdateLoading,
        markPendingRefreshFromLoadResult,
        requestPosSizingContextUpdate,
        riskCommandsRef,
        setAcceptedFactorContext,
        setSnackbarMessage,
        setSnackbarOpen,
        setSnackbarSeverity,
        user?.uid,
    ]);

    const handlePosSizingScatterPointSelect = useCallback((rowId) => {
        if (!rowId) return;
        setPosSizingLinkedRowId(rowId);
        setPosSizingLinkedRowRequestId(currentRequestId => currentRequestId + 1);
    }, []);

    useImportApplySync({
        requestPosSizingContextUpdate,
        userUid: user?.uid,
        actualPortfolioVersion,
        setActualPortfolioVersion,
        riskCommandsRef,
        markPendingRefreshFromLoadResult,
        autoRefreshEnabledRef,
        refreshPriceDataRef,
        importApplyInFlightRef,
        posSizingSyncInFlightRef,
    });

    // A reload would still read the superseded accepted context; following
    // the saved setting reloads the page instead.
    const publishedFactorSettingAwaitingAcceptance = isPublishedFactorSettingAwaitingAcceptance({
        publishedContextStatus: publishedWatchlistContextStatusRef.current,
        acceptedFactorContextKey,
        configuredContext: {
            factorModel: selectedFactorModel,
            factorDataModel: requestedFactorDataModel,
        },
        activeContext: {
            factorModel: activeSelectedFactorModel,
            factorDataModel: activeRequestedFactorDataModel,
        },
    });

    // Single orchestrated load: actual portfolio id -> matrices -> derived data
    useCoreLoadBootstrap({
        userUid: user?.uid,
        userData,
        bootstrapSelectionsReady,
        // Bootstrap reads the live exclusive owner itself. Keep this captured
        // flag for independent factor gates only, so a ready-render snapshot
        // cannot clear the completed key after the publication releases owner.
        suspendCoreLoad: factorDataUpdateLoading
            || publishedFactorSettingAwaitingAcceptance,
        selectedFactorModel,
        requestedFactorDataModel,
        lastCoreLoadKeyRef,
        publicationCoreLoadCommitVersionRef,
        coreLoadInFlightRef,
        exclusiveCoreLoadOwnerRef,
        beginCoreLoadVisual,
        endCoreLoadVisual,
        riskCommandsRef,
        markPendingRefreshFromLoadResult,
        onCommitWatchlistBootstrapRevision,
        consumeQueuedPriceRefresh,
        refreshPriceDataRef,
        watchlistActualSyncVersion,
    });

    usePosSizingFreshnessPrompt({
        userUid: user?.uid,
        actualPortfolioIsReady,
        activeView: posSizingSyncRequiredPrompt ? 'sync-required' : activeView,
        posSizingSyncStatus,
        posSizingSyncStatusLoading,
        posSizingLoading,
        priceRefreshLoading,
        pendingPosSizingAutoSync,
        contextUpdateState,
        autoSyncPreferencesReady: posSizingSyncPreferences.ready,
        posSizingAutoSyncEnabled,
        setError,
        setPosSizingFreshnessDetailsOpen,
        posSizingEntrySequenceRef,
        posSizingFreshnessPromptStateRef,
    });

    usePosSizingDraftConsumer({
        activeView,
        optimizerViewMode: viewMode,
        watchlistVersion,
        posSizingSaving: posSizingTableSaving,
        posSizingPendingChangesRef,
        posSizingEditingRef,
        // A known missing-durable-context response is an actionable state, not
        // a polling condition. The explicit Sync Draft command performs the
        // next workspace read after it publishes from Actual.
        workspaceAvailable: posSizingSyncRequiredPrompt || !posSizingStartingValuesReady
            ? false
            : watchlistIsReady,
        posSizingLoading,
        posSizingLoaded,
        fetchPosSizingData,
    });

    const posSizingDraftFreshnessSummary = buildPosSizingDraftFreshnessSummary(
        posSizingSyncStatus,
        {
            actualPortfolioIsReady,
            actualPortfolioRows: combinedHoldings,
            watchlistRows: posSizingSyncWatchlistRows,
        },
    );
    const posSizingSyncRequiredSummary = posSizingSyncRequiredPrompt ? {
        visible: true,
        loading: false,
        isStale: true,
        statusColor: 'warning',
        freshnessLabel: 'Sync Required',
        detailText: posSizingSyncRequiredPrompt.message,
        contextState: 'unavailable',
        contextComparisonRows: [],
        syncActionVisible: !getPosSizingActualSourceBlocker(
            posSizingSyncStatus, actualPortfolioIsReady, posSizingSyncWatchlistRows,
        ),
        importRows: posSizingDraftFreshnessSummary.importRows || [],
        removalRows: posSizingDraftFreshnessSummary.removalRows || [],
        draftOnlyRows: posSizingDraftFreshnessSummary.draftOnlyRows || [],
    } : { visible: false };
    const hasPortfolioData = Boolean(actualPortfolioIsReady);
    const hasPosSizingData = Array.isArray(posSizingData) && posSizingData.length > 0;
    const promoteMandatoryFactorContextFromLoadResult = useCallback((loadResult, reason, options = {}) => {
        if (!isReadyFactorMatrixRefreshResult(loadResult)) {
            return false;
        }
        const refreshedContext = (
            loadResult?.factorContextOverride
            || loadResult?.matricesOverride
            || loadResult?.factorContext
            || null
        );
        if (!getFactorContextKey(refreshedContext)) {
            return false;
        }
        const acceptedNext = inheritFactorGenerationDigest(
            { ...refreshedContext, reason }, acceptedFactorContext, factorDataFreshnessStatus,
        );
        const apply = () => {
            setAcceptedFactorContext(acceptedNext);
            setPendingFactorContext(null);
            setDismissedPendingFactorContextKey('');
            setFactorDataDetailsOpen(false);
        };
        if (options.stagePublicationState) options.stagePublicationState(apply, options.isCurrentRequest);
        else apply();
        return true;
    }, [acceptedFactorContext, factorDataFreshnessStatus, setAcceptedFactorContext]);
    const markPublicationCoreLoadCommitted = useCallback((_loadResult, preparedTarget) => {
        lastCoreLoadKeyRef.current = preparedTarget.bootstrapPublicKey;
        publicationCoreLoadCommitVersionRef.current += 1;
    }, []);
    const showFactorSettingNotice = useCallback((message, severity) => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    }, []);
    const {
        followPublishedFactorSetting,
        handleFactorContextChanged,
    } = usePublishedFactorSettingFollow({
        userUid: user?.uid,
        userData,
        selectedFactorModelRef,
        selectedAxiomaModelRef,
        selectedBarraModelRef,
        acceptedFactorContextRef,
        exclusiveCoreLoadOwnerRef,
        coreLoadInFlightRef,
        lastCoreLoadKeyRef,
        riskCommandsRef,
        promoteFactorContext: promoteMandatoryFactorContextFromLoadResult,
        markPendingRefreshFromLoadResult,
        requestPosSizingContextUpdate,
        setFactorDataUpdateLoading,
        beginCoreLoadVisual,
        endCoreLoadVisual,
        consumeQueuedPriceRefresh,
        refreshPriceDataRef,
        onUserDataChange,
        onNotice: showFactorSettingNotice,
    });
    followPublishedFactorSettingRef.current = followPublishedFactorSetting;
    const optimizerPreparationInputsRef = useRef(null);
    optimizerPreparationInputsRef.current = { aum, actualPortTradeIdeas };
    const preparePublicationTarget = useCallback(async ({
        loadResult, publication, durableUserData, publicationVersionTransition, stagePublicationState, isCurrentRequest, actualSyncApplied,
    }) => {
        const scopeKey = loadedFactorScopeKey;
        const baselinePosVersion = posSizingVersionRef.current;
        const sourceInputs = optimizerPreparationInputsRef.current;
        let plannedPosVersion = baselinePosVersion;
        let acceptedPosVersion = baselinePosVersion;
        const savedBases = new Map();
        const discardCallbacks = [];
        let discarded = false;
        const canCommit = () => isCurrentRequest()
            && loadedFactorScopeKeyRef.current === scopeKey
            && !discarded && posSizingVersionRef.current === acceptedPosVersion
            && optimizerPreparationInputsRef.current.aum === sourceInputs.aum
            && optimizerPreparationInputsRef.current.actualPortTradeIdeas === sourceInputs.actualPortTradeIdeas;
        const approved = publication?.factor_context ?? publication?.lifecycle?.factor_context;
        const status = await fetchWatchlistContextStatus();
        if (!canCommit()) return null;
        const canonical = status?.saved ? { ...status.saved } : null;
        if (loadResult.matrixStatus !== 'empty_watchlist') {
            // Status.saved is a 16-field comparison projection, without an ID.
            // Core consumed the exact revision's industry/quote receipt; use
            // that verified identity rather than inventing one from settings.
            const consumedFactorContextId = loadResult.preparedState?.watchlistFactorContextId;
            const vendor = normalizeUserFactorModel(approved?.provider_vendor);
            if (!canonical || normalizeUserFactorModel(canonical.vendor) !== vendor
                || Number(canonical.factor_release_id) !== Number(approved?.factor_release_id)
                || String(canonical.factor_as_of_date || '') !== String(approved?.factor_as_of_date || '')
                || String(canonical.factor_model || '').trim().toUpperCase()
                    !== String(approved?.factor_model || '').trim().toUpperCase()
                || String(canonical.factor_horizon ?? '').trim().toUpperCase()
                    !== String(approved?.factor_horizon ?? '').trim().toUpperCase()
                || !consumedFactorContextId
                || consumedFactorContextId !== approved?.factor_context_id
                || (canonical.factor_context_id != null && canonical.factor_context_id !== consumedFactorContextId)) {
                throw new Error('The published factor context could not be verified for the final reveal.');
            }
        }
        const actualSurface = loadResult.preparedState?.actualSurface;
        const holdingsData = (actualSurface?.holdingsResult?.holdingsCalculations ?? []).map(h => ({
            id: h.cognive_id ?? h.id ?? h.ticker ?? null,
            sedol: h.display_sedol ?? h.sedol ?? null,
            display_sedol: h.display_sedol ?? h.sedol ?? null,
            ...h,
        }));
        const target = {
            actualRows: combineHoldings({ holdings: holdingsData,
                positions: actualSurface?.positionsResult?.positions ?? [],
                actualPortTradeIdeas: sourceInputs.actualPortTradeIdeas }),
            optimizerInputs: {
                selectedFactorModel: formatUserFactorModel(loadResult.matricesOverride?.factorModel),
                selectedBetaSource: loadResult.matricesOverride?.betaSource,
                factorSnapshotDate: loadResult.matricesOverride?.factorSnapshotDate,
                factorDataModel: loadResult.matricesOverride?.factorDataModel,
                optimizerIdioVolSource: loadResult.matricesOverride?.idioVolSource,
                selectedAxiomaModel: durableUserData?.axioma_model
                    ? normalizeAxiomaFactorDataModel(durableUserData.axioma_model) : selectedAxiomaModel,
                selectedBarraModel: durableUserData?.barra_model
                    ? normalizeBarraFactorDataModel(durableUserData.barra_model) : selectedBarraModel,
                aum: sourceInputs.aum,
            },
            factorContext: loadResult.matricesOverride,
            canonicalFactorContext: canonical,
            matrixStatus: loadResult.matrixStatus,
            actualSurface: loadResult.preparedState?.actualSurface ?? null,
            watchlistRows: loadResult.preparedState?.watchlistRows ?? [],
            actualPortfolioIsReady: loadResult.preparedState?.actualPortfolioIsReady === true,
            watchlistIsReady: loadResult.preparedState?.watchlistIsReady === true,
            factorSliceUserUid: user?.uid,
            factorSliceScopeKey: scopeKey,
            watchlistRevision: loadResult.watchlistSurface?.watchlistRevision,
            canCommit,
            registerDiscard: (callback) => { if (!discarded && typeof callback === 'function') discardCallbacks.push(callback); },
            discard: () => {
                if (discarded) return;
                discarded = true;
                discardCallbacks.splice(0).forEach(callback => callback());
            },
            get versions() { return {
                watchlistVersion: publicationVersionTransition.watchlistVersion,
                actualPortfolioVersion: publicationVersionTransition.actualPortfolioVersion,
                posSizingVersion: plannedPosVersion,
            }; },
            reservePosSizingVersion: (base) => {
                if (!canCommit() || base?.portfolio_row_version == null) {
                    throw new Error('The prepared child version lost its publication scope.');
                }
                const key = JSON.stringify([base.portfolio_id, base.portfolio_row_version, base.watchlist_revision_id]);
                if (!savedBases.has(key)) savedBases.set(key, ++plannedPosVersion);
                return savedBases.get(key);
            },
            acceptPosSizingVersion: (version) => { acceptedPosVersion = version; },
            prepareSelectedOverview: async (coreResult, nextTarget) => {
                const current = readSelectedOverviewInputs();
                const nextScope = resolveSelectedOverviewScope({
                    ...current,
                    portfolios: nextTarget.optimizerSummaries?.portfolios ?? current.portfolios,
                    watchlistVersion: nextTarget.versions.watchlistVersion,
                    posSizingVersion: nextTarget.versions.posSizingVersion,
                    riskContextInputs: buildOverviewRiskContextInputs({
                        acceptedFactorContext: nextTarget.factorContext,
                        factorDataModel: nextTarget.factorContext?.factorDataModel,
                        factorSnapshotDate: nextTarget.factorContext?.factorSnapshotDate,
                        betaSource: nextTarget.factorContext?.betaSource,
                        idioVolSource: nextTarget.factorContext?.idioVolSource,
                        posSizingAutoSyncEnabled: posSizingAutoSyncEnabledRef.current,
                        posSizingSyncStatus: nextTarget.workspace?.pos_sizing_sync_status ?? posSizingSyncStatusRef.current,
                    }),
                });
                if (activeView === 'overview' && nextScope) {
                    if (await coreResult.prepareSelectedOverview(nextScope, nextTarget.versions.posSizingVersion) !== true) {
                        throw new Error('The selected Overview could not prepare its final resource scope.');
                    }
                }
            },
        };
        const futureSelectedModel = formatUserFactorModel(durableUserData?.factor_model ?? selectedFactorModel);
        target.bootstrapPublicKey = coreLoadPublicKey(user?.uid || '', futureSelectedModel,
            getEffectiveFactorDataModel(futureSelectedModel,
                target.optimizerInputs.selectedAxiomaModel, target.optimizerInputs.selectedBarraModel));
        // This is the same persisted/user-chosen resolver as normal Actual sync;
        // no sizing values are inferred from the old financial panel state.
        if (actualSyncApplied) {
            target.actualStartingValues = await posSizingStartingValues.resolveActualSyncStartingValues();
        }
        if (!canCommit()) return null;
        // These local inputs also participate in resource and baseline keys.
        // Install their validated next values with the financial bundle rather
        // than waiting for the userData synchronization effects after reveal.
        const nextBetaSource = normalizeBetaSource(durableUserData?.beta_source);
        const nextIdioVolSource = normalizeIdioVolSource(
            durableUserData?.idio_vol_source ?? optimizerIdioVolSourceRef.current,
        );
        stagePublicationState(() => {
            setSelectedFactorModel(futureSelectedModel);
            setSelectedAxiomaModel(target.optimizerInputs.selectedAxiomaModel);
            setSelectedBarraModel(target.optimizerInputs.selectedBarraModel);
            setSelectedBetaSource(nextBetaSource);
            setOptimizerIdioVolSource(nextIdioVolSource);
            visiblePerformanceContextRef.current = { scopeKey, context: canonical };
        }, canCommit);
        return target;
    }, [activeView, loadedFactorScopeKey, posSizingStartingValues.resolveActualSyncStartingValues,
        posSizingVersionRef, readSelectedOverviewInputs, selectedFactorModel, selectedAxiomaModel, selectedBarraModel,
        setSelectedFactorModel, setSelectedAxiomaModel, setSelectedBarraModel,
        setSelectedBetaSource, setOptimizerIdioVolSource, user?.uid]);
    const prepareMountedPublication = useCallback(async (options) => {
        if (activeViewRef.current === 'optimizer') {
            if (typeof prepareOptimizerPublication !== 'function') {
                throw new Error('The visible Optimizer has no prepared contract reader.');
            }
            await prepareOptimizerPublication(options);
            return null;
        }
        if (activeViewRef.current !== 'performance') return null;
        const prepare = mountedPublicationPreparationRef.current;
        if (activeViewRef.current === 'performance' && !prepare) {
            throw new Error('The visible Performance workspace has not registered its preparation boundary.');
        }
        if (!prepare) return null;
        try {
            return await prepare(options);
        } catch (error) {
            if (error?.code === 'performance_preparation_obsolete'
                && options.isCurrentRequest()
                && mountedPublicationPreparationRef.current !== prepare
                && activeViewRef.current !== 'performance') return null;
            throw error;
        }
    }, [prepareOptimizerPublication]);
    const {
        showWatchlistUploadGuide,
        showPortfolioImportGuide,
        handleWatchlistGuideInteraction,
        handlePortfolioGuideInteraction,
        handleWatchlistUploadSuccess,
    } = useWatchlistWorkflow({
        requestPosSizingContextUpdate,
        userUid: user?.uid,
        activeView,
        setupGuideDismissed,
        watchlistIsReady,
        watchlistLoading: industriesLoading,
        actualPortfolioIsReady,
        hasPosSizingData,
        watchlistGuideAcknowledged,
        setWatchlistGuideAcknowledged,
        portfolioGuideAcknowledged,
        setPortfolioGuideAcknowledged,
        updateSetupGuideState,
        onUserDataChange,
        currentBetaSource: selectedBetaSource,
        beginPublicationVersionTransition,
        preparePublicationTarget,
        prepareMountedPublication,
        riskCommandsRef,
        markPendingRefreshFromLoadResult,
        onMandatoryFactorContextLoaded: promoteMandatoryFactorContextFromLoadResult,
        onPublicationCoreLoadCommitted: markPublicationCoreLoadCommitted,
        publishedBetaContextRef,
    });

    const latestWatchlistPublicationExecutorRef = useRef(handleWatchlistUploadSuccess);
    useLayoutEffect(() => {
        latestWatchlistPublicationExecutorRef.current = handleWatchlistUploadSuccess;
    }, [handleWatchlistUploadSuccess]);
    const stableWatchlistPublicationExecutor = useCallback((detail, executionContext) => (
        latestWatchlistPublicationExecutorRef.current(detail, executionContext)
    ), []);

    useLayoutEffect(() => {
        if (typeof onRegisterWatchlistPublicationExecutor !== 'function') {
            return undefined;
        }
        return onRegisterWatchlistPublicationExecutor(stableWatchlistPublicationExecutor);
    }, [
        onRegisterWatchlistPublicationExecutor,
        stableWatchlistPublicationExecutor,
        user?.uid,
    ]);

    useEffect(() => {
        const handleMsiSecurityResolutionApplied = (event) => {
            const detail = event?.detail || {};
            if (!isSecurityOperationEventForScope(
                detail,
                securityOperationSessionScopeToken,
            )) return;
            let lifecycle;
            try {
                lifecycle = normalizeWatchlistLifecycle(detail);
            } catch (error) {
                console.error('Ignoring an invalid Watchlist lifecycle.', error);
                return;
            }
            if (
                !['succeeded', 'unchanged'].includes(lifecycle.status)
                || lifecycle.phase !== 'complete'
            ) {
                return;
            }
            const publicationReceipt = parseWatchlistPublicationReceipt(lifecycle);
            const classificationResult = parseWatchlistDeltaClassification(lifecycle);
            if (classificationResult.error) {
                console.error(classificationResult.error);
                return;
            }
            const { classification: deltaClassification } = classificationResult;
            if (deltaClassification === WATCHLIST_DELTA_CLASSIFICATIONS.NO_CHANGE) {
                return;
            }
            if (!publicationReceipt) {
                console.warn('Ignoring a settled Watchlist change without a valid revision receipt.');
                return;
            }
            if (
                !requiresWatchlistClientPublication(detail)
                && (
                    deltaClassification
                        === WATCHLIST_DELTA_CLASSIFICATIONS.CATEGORIZATION_CHANGED
                    || deltaClassification
                        === WATCHLIST_DELTA_CLASSIFICATIONS.CUSTOM_METADATA_CHANGED
                )
            ) {
                const commands = getRiskCommandsOrThrow(
                    riskCommandsRef,
                    'Dashboard.handleMsiSecurityResolutionApplied',
                    ['refreshSelectedOverview'],
                );
                advanceWatchlistVersion();
                const statusRefresh = refreshAuthoritativePosSizingStatus();
                void Promise.resolve(statusRefresh).then(() => (
                    requestPosSizingContextUpdate({
                        reason: 'watchlist_attributes_changed',
                    })
                )).catch((error) => {
                    console.error(
                        'Position Sizing auto-sync after a Watchlist attribute change failed.',
                        error,
                    );
                }).finally(() => {
                    // The Watchlist version and Position Sizing status changed.
                    commands.refreshSelectedOverview({ reason: 'watchlist_attributes_changed' });
                });
                void refreshIndustryMetadata();
                return;
            }

            // Membership publication is observed and committed by the
            // always-mounted app-shell coordinator. This view-local listener
            // owns only metadata-only refreshes.
        };

        window.addEventListener(
            MSI_SECURITY_RESOLUTION_APPLIED_EVENT,
            handleMsiSecurityResolutionApplied,
        );
        return () => {
            window.removeEventListener(
                MSI_SECURITY_RESOLUTION_APPLIED_EVENT,
                handleMsiSecurityResolutionApplied,
            );
        };
    }, [
        advanceWatchlistVersion,
        refreshAuthoritativePosSizingStatus,
        refreshIndustryMetadata,
        requestPosSizingContextUpdate,
        securityOperationSessionScopeToken,
    ]);

    const {
        posSizingOptimizerContextControl,
        posSizingImportsControl,
        watchlistUploadControl,
        watchlistM365SyncControl,
        watchlistReviewHandoff,
        handleWatchlistReviewHandoff,
    } = useDashboardUploadControls({
        posSizingDraftFreshnessSummary,
        handleSyncPosSizingDraft: handleUserSyncPosSizingDraft,
        posSizingLoading,
        posSizingDraftSyncLoading,
        priceRefreshLoading,
        posSizingSyncStatusLoading,
        posSizingFreshnessDetailsOpen,
        handlePosSizingFreshnessDetailsOpenChange,
        activeView,
        defaultTargetVolPct,
        defaultTargetVolDollar,
        defaultScaler,
        buildPosSizingPublicationFields: buildFreshContextPublicationFields,
        handlePosSizingUploadSuccess: handlePosSizingFileUploadSuccess,
        handlePortfolioGuideInteraction,
        handleWatchlistGuideInteraction,
        setWatchlistUploadBusy,
        watchlistActive: activeView === 'watchlist',
        watchlistOperationActive,
    });
    useWatchlistReviewNavigationHandoff({
        location,
        navigate,
        onReviewHandoff: handleWatchlistReviewHandoff,
    });
    const handleWatchlistContextError = useCallback((message) => {
        setSnackbarMessage(message);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
    }, []);
    const handleWatchlistContextNotice = useCallback((message) => {
        setSnackbarMessage(message);
        setSnackbarSeverity('info');
        setSnackbarOpen(true);
    }, []);
    const factorDataSyncPreferences = useFactorDataSyncPreferences(user?.uid, handleWatchlistContextError);
    const factorDataAutoSyncEnabled = factorDataSyncPreferences.enabled;
    const setFactorDataAutoSyncEnabled = factorDataSyncPreferences.setEnabled;
    const watchlistContextSync = useWatchlistContextSync({
        autoSyncEnabled: factorDataAutoSyncEnabled,
        preferencesReady: factorDataSyncPreferences.ready,
        operationActive: watchlistContextUpdatePending,
        operationStatus: securityOperationStatus,
        publicationState: watchlistPublicationState,
        optimizerSummaryStatus,
        userUid: user?.uid,
        watchlistVersion,
        sessionScopeToken: securityOperationSessionScopeToken,
        factorSettingKey: [
            userData?.missing_factor_policy,
            userData?.axioma_model,
            userData?.barra_model,
        ].map((value) => String(value || '')).join('|'),
        onReviewHandoff: handleWatchlistReviewHandoff,
        onError: handleWatchlistContextError,
        onNotice: handleWatchlistContextNotice,
    });
    const publicationPresentationPending = watchlistContextUpdatePending;
    if (!publicationPresentationPending && watchlistContextSync.currentStatus?.saved) {
        visiblePerformanceContextRef.current = { scopeKey: loadedFactorScopeKey, context: watchlistContextSync.currentStatus.saved };
    }
    const displayedPerformanceContext = visiblePerformanceContextRef.current.context;
    const { applyFactorVendorChange } = watchlistContextSync;
    const factorDatasetCatalogue = useFactorDatasetCatalogue({ userUid: user?.uid, watchlistVersion });
    publishedWatchlistContextStatusRef.current = watchlistContextSync.status;

    useOptimizerPortfolioDropdownBootstrap({
        posSizingLabel: POS_SIZING_PORTFOLIO_LABEL,
        setOptimalPortfolioDropdownValues,
        setPosSizingPortfolioId,
    });

    const {
        applyOptimizerIdioVolSourceChange,
    } = useOptimizerIdioSourceChange({
        requestPosSizingContextUpdate,
        optimizerIdioVolSource,
        setOptimizerIdioVolSource,
        riskCommandsRef,
    });

    const handleRiskContextRefreshRequest = useCallback(async ({
        idioVolSource,
        factorVendorTransition,
    } = {}) => {
        let refreshed = false;
        if (idioVolSource) {
            await applyOptimizerIdioVolSourceChange(idioVolSource);
            refreshed = true;
        }
        // Another factor vendor confirmed in Settings is the user's approval
        // of its data: the same re-check as "Use new factor data" runs now,
        // and the vendor is saved when the switch publishes. Only a Watchlist
        // saved before context tracking still opens a review.
        const vendorChange = applyFactorVendorChange?.(factorVendorTransition);
        if (vendorChange === 'review') navigate('/dashboard/watchlist');
        return Boolean(vendorChange) || refreshed;
    }, [
        applyFactorVendorChange,
        applyOptimizerIdioVolSourceChange,
        navigate,
    ]);

    const handleRiskOverviewEffectiveContextChange = useCallback(async ({ riskContext } = {}) => {
        const sameFactorSelection = (
            formatUserFactorModel(riskContext?.factorModel) === actualRiskOverviewScope.factorModel
            && normalizeContextFactorDataModel(
                riskContext?.factorModel,
                riskContext?.factorDataModel,
            ) === actualRiskOverviewScope.factorDataModel
            && normalizeBetaSource(riskContext?.betaSource) === actualRiskOverviewScope.betaSource
        );
        if (!sameFactorSelection) {
            return false;
        }
        if (riskContext?.factorSnapshotDate !== actualRiskOverviewScope.factorSnapshotDate) {
            setFactorDataStatusRefreshSignal((value) => value + 1);
            return true;
        }
        if (
            normalizeIdioVolSource(riskContext?.idioVolSource)
            !== actualRiskOverviewScope.idioVolSource
        ) {
            return applyOptimizerIdioVolSourceChange(riskContext.idioVolSource);
        }
        return false;
    }, [
        actualRiskOverviewScope,
        applyOptimizerIdioVolSourceChange,
        normalizeContextFactorDataModel,
    ]);

    // Poll lightweight server readiness while the actual portfolio is pending.
    // A changed snapshot is published through the existing atomic positions,
    // holdings, and sector owners; effective-context changes return to the
    // established factor/idiosyncratic context owners before any publication.
    useRiskOverviewReadinessPolling({
        scope: actualRiskOverviewScope,
        coverage: calculations?.risk_calculation_coverage || null,
        fetchPositions,
        refreshRiskOverview: riskCommands.refreshActualRiskLens,
        onEffectiveRiskContextChange: handleRiskOverviewEffectiveContextChange,
        refreshRequired: Boolean(
            startupSecondaryLoadsReady
            && calculations
            && calculations?.riskOverviewResource?.scopeKey
                !== actualRiskOverviewScopeKey
        ),
        enabled: activeView === 'overview',
    });

    useBetaContextRefresh({
        requestPosSizingContextUpdate,
        activeView,
        userUid: user?.uid,
        userData,
        bootstrapSelectionsReady,
        effectiveBetaContextKey,
        selectedBetaSource,
        selectedFactorModel: activeSelectedFactorModel,
        requestedFactorDataModel: activeRequestedFactorDataModel,
        markPendingRefreshFromLoadResult,
        riskCommandsRef,
        beginCoreLoadVisual,
        endCoreLoadVisual,
        betaSourceTransitionLoading,
        setBetaSourceTransitionLoading,
        betaSourceRefreshReadyRef,
        lastEffectiveBetaContextKeyRef,
        lastBetaRefreshDriverRef,
        betaContextRefreshRunIdRef,
        publishedBetaContextRef,
    });

    const { refreshPriceData } = usePriceRefreshFlow({
        userUid: user?.uid,
        autoRefreshEnabled,
        autoRefreshEnabledRef,
        matricesLoading,
        riskCommandsRef,
        refreshPriceDataRef,
        coreLoadInFlightRef,
        exclusiveCoreLoadOwnerRef,
        priceRefreshInFlightRef,
        importApplyInFlightRef,
        posSizingAutoSyncEnabledRef,
        queuePriceRefresh,
        consumeQueuedPriceRefresh,
        markPendingRefreshFromLoadResult,
        requestPosSizingContextUpdate,
        fetchPriceRefreshStatus,
        notifyPendingPriceRefresh,
        setPriceRefreshLoading,
        setPendingAutoRefresh,
        posSizingIdioVolSource,
        forceNextMatricesReloadRef,
    });

    const handleAutoRefreshEnabledChange = useCallback((enabled) => {
        const nextEnabled = Boolean(enabled);
        const wasEnabled = autoRefreshEnabledRef.current;
        autoRefreshEnabledRef.current = nextEnabled;
        setAutoRefreshEnabled(nextEnabled);

        if (
            nextEnabled
            && !wasEnabled
            && pendingAutoRefresh?.has_newer_prices
        ) {
            void refreshPriceDataRef.current?.({ reason: 'auto_price_refresh' });
        }
    }, [
        autoRefreshEnabledRef,
        pendingAutoRefresh?.has_newer_prices,
        refreshPriceDataRef,
    ]);

    const handleOptimizerRecoveryNotice = useCallback((message, severity) => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    }, []);
    const optimizerRecovery = useOptimizerWatchlistRecovery({
        userUid: user?.uid,
        sessionScopeToken: securityOperationSessionScopeToken,
        publicationState: watchlistPublicationState,
        fetchOptimizerPortfolios,
        onNotice: handleOptimizerRecoveryNotice,
    });
    const { continueAfterPublication } = optimizerRecovery;
    const handleOptimizerWatchlistRestorationStaged = useCallback((response, recovery) => {
        if (!continueAfterPublication(response, recovery)) return;
        setOverviewOptimizerRestorationPortfolio(null);
        if (response.status === 'not_needed') return;
        handleWatchlistReviewHandoff({
            origin: 'optimizer_restoration',
            status: 'changes_awaiting_review',
            revisionId: response.watchlist_revision_id,
            sessionScopeToken: securityOperationSessionScopeToken,
        });
        navigate('/dashboard/watchlist');
    }, [continueAfterPublication, handleWatchlistReviewHandoff, navigate, securityOperationSessionScopeToken]);

    const { overviewPriceFreshness } = useOverviewPriceFreshness({
        userUid: user?.uid,
        activeView,
        combinedHoldings,
        liveQuotesByCogniveId,
        overviewSnapshotAt,
        liveQuotesLastUpdatedAt,
        autoRefreshEnabled,
        priceRefreshLoading,
        matricesLoading,
        fetchPriceRefreshStatus,
        refreshPriceData,
        notifyPendingPriceRefresh,
        setPendingAutoRefresh,
        importApplyInFlightRef,
        priceRefreshInFlightRef,
        coreLoadInFlightRef,
        lastImmediatePriceProbeRef,
        priceWatermarkBaseline: lastAppliedPriceWatermarkRef.current,
    });

    const {
        posSizingAutoSyncToggleBusy,
        posSizingWorkspaceBusy,
        handlePriceRefreshRequest,
        priceSyncControl,
        handleFactorDataRefreshRequest: handleDirectFactorDataRefreshRequest,
        appBarUploadContext,
        appBarSyncState,
    } = useOverviewSyncControls({
        userUid: user?.uid,
        autoRefreshEnabled,
        pendingAutoRefresh,
        actualPortfolioIsReady,
        coreLoadVisualLoading,
        coreLoadInFlightRef,
        holdingsLoading,
        calculationsLoading,
        matricesLoading,
        positionsLoading,
        hasPortfolioData,
        overviewPriceFreshness,
        priceRefreshLoading,
        betaSourceTransitionLoading,
        viewModeLoading: optimizerBaselineLoading,
        posSizingLoading,
        pendingPosSizingAutoSync,
        posSizingSyncInFlightRef,
        posSizingTableSaving,
        posSizingAutoSyncEnabled,
        refreshPriceData,
        importApplyInFlightRef,
        priceRefreshInFlightRef,
        riskCommandsRef,
        setSnackbarMessage,
        setSnackbarSeverity,
        setSnackbarOpen,
        setPriceRefreshLoading,
        selectedFactorModel: activeSelectedFactorModel,
        normalVol,
        maxPosSize,
        aum,
        defaultTargetVolPct,
        defaultTargetVolDollar,
        defaultScaler,
        watchlistIsReady,
    });

    const handleFactorDataRefreshRequest = useCallback(async () => {
        if (watchlistContextSync.status?.saved) {
            if (hasFactorDataUpdate(watchlistContextSync.status)) await watchlistContextSync.applyUpdate();
            return;
        }
        if (pendingFactorContextKey) {
            handleLoadPendingFactorData();
            return;
        }
        const latestStatusKey = getFactorContextKey(factorDataFreshnessStatus);
        if (latestStatusKey && latestStatusKey !== acceptedFactorContextKey) {
            beginFactorContextAcceptance(buildConfiguredFactorContext(
                factorDataFreshnessStatus,
                'inventory_changed',
            ));
            return;
        }
        await handleDirectFactorDataRefreshRequest();
    }, [
        watchlistContextSync,
        acceptedFactorContextKey,
        beginFactorContextAcceptance,
        buildConfiguredFactorContext,
        factorDataFreshnessStatus,
        handleDirectFactorDataRefreshRequest,
        handleLoadPendingFactorData,
        pendingFactorContextKey,
    ]);

    const publishedFactorUpdate = hasFactorDataUpdate(watchlistContextSync.status);
    const publishedFactorCurrent = ['current', 'update_available'].includes(watchlistContextSync.status?.status)
        && !publishedFactorUpdate;
    const factorDataFreshnessSummary = watchlistContextSync.status?.saved ? {
        visible: true,
        contextState: publishedFactorUpdate ? 'update_available' : publishedFactorCurrent ? 'current' : 'unavailable',
        loading: watchlistContextSync.updateInProgress,
        statusColor: publishedFactorUpdate ? 'warning' : publishedFactorCurrent ? 'success' : 'info',
        statusLabel: 'Factor Data',
        freshnessLabel: publishedFactorUpdate ? 'Update available' : publishedFactorCurrent ? 'Current' : 'Comparison unavailable',
        detailText: watchlistContextSync.updateInProgress
            ? watchlistContextSync.updateProgressMessage
            : publishedFactorUpdate
                ? factorDataUpdateExplanation(watchlistContextSync.status)
                : 'Your Watchlist and Actual Portfolio use the current accepted factor data.',
        acceptedContextSummary: formatFactorDataContextSummary(factorDataDisplayContext(watchlistContextSync.status.saved)),
        pendingContextSummary: publishedFactorUpdate
            ? formatFactorDataContextSummary(factorDataDisplayContext(watchlistContextSync.status.available)) : null,
        showLoadAction: publishedFactorUpdate,
        metadataChips: [],
    } : buildFactorDataFreshnessSummary({
        acceptedContext: acceptedFactorContext,
        pendingContext: pendingFactorContext,
        loading: factorDataUpdateLoading || matricesLoading,
        availableStatus: factorDataFreshnessStatus,
    });
    const factorDataLoadDisabled = Boolean(
        watchlistContextSync.dateSelectionBlocked ||
        watchlistContextSync.updateInProgress ||
        factorDataUpdateLoading ||
        matricesLoading ||
        coreLoadInFlightRef.current ||
        priceRefreshInFlightRef.current ||
        importApplyInFlightRef.current
    );
    const factorDataLoadBlockedReason = !factorDataLoadDisabled ? null
        : watchlistPublicationState?.lifecycle === 'needs_attention'
            ? 'The data update needs attention. Reload the app before choosing another factor date.'
            : securityOperationStatus?.phase === 'removal_review'
                ? 'Review the Watchlist changes before choosing another factor date.'
                : watchlistContextUpdatePending || watchlistContextSync.busy
                    || watchlistContextSync.updateInProgress || factorDataUpdateLoading || matricesLoading
                    ? 'Loading factor data and checking your Watchlist. Date selection unlocks as soon as the updated app data is ready.'
                    : 'Refreshing portfolio data. Date selection unlocks as soon as the refresh finishes.';
    const handlePublishedFactorDataDetailsOpenChange = useCallback((open) => {
        if (open && watchlistContextSync.status?.saved) watchlistContextSync.dismissPrompt();
        handleFactorDataDetailsOpenChange(open);
    }, [watchlistContextSync.dismissPrompt, watchlistContextSync.status?.saved, handleFactorDataDetailsOpenChange]);
    const appBarUploadContextWithFactorData = useMemo(() => ({
        ...appBarUploadContext,
        factorDataFreshnessSummary,
        factorDataLoadDisabled,
        factorDataLoadBlockedReason,
        factorDataLoadInProgress: factorDataUpdateLoading || watchlistContextSync.updateInProgress || watchlistContextSync.busy,
        factorContextUpdateProgress: watchlistContextSync.progressContext,
        factorDatasetCatalogue,
        onSelectFactorDataset: watchlistContextSync.selectFactorDataset,
        factorDataDetailsOpen,
        onFactorDataDetailsOpenChange: handlePublishedFactorDataDetailsOpenChange,
    }), [
        appBarUploadContext,
        factorDataDetailsOpen,
        factorDataFreshnessSummary,
        factorDataLoadDisabled,
        factorDataLoadBlockedReason,
        factorDataUpdateLoading,
        watchlistContextSync.updateInProgress,
        watchlistContextSync.progressContext,
        watchlistContextSync.busy,
        watchlistContextSync.selectFactorDataset,
        factorDatasetCatalogue,
        handlePublishedFactorDataDetailsOpenChange,
    ]);

    const appBarSyncStateWithFactorData = useMemo(() => ({
        ...appBarSyncState,
        factorDataAutoSyncEnabled,
        factorDataSyncPreferencesReady: factorDataSyncPreferences.ready,
        posSizingAutoSyncToggleBusy: appBarSyncState.posSizingAutoSyncToggleBusy || !posSizingSyncPreferences.ready,
    }), [appBarSyncState, factorDataAutoSyncEnabled, factorDataSyncPreferences.ready, posSizingSyncPreferences.ready]);

    const appContextLineage = useMemo(() => {
        const watchlistStatus = watchlistContextSync?.status ?? null;
        const posSizingContext = posSizingSyncStatus?.optimizer_context ?? null;
        const acceptedFactorDataContext = {
            factorModel: acceptedFactorContext?.factorModel
                ?? activeSelectedFactorModel,
            factorDataModel: acceptedFactorContext?.factorDataModel
                ?? activeRequestedFactorDataModel,
            factorSnapshotDate: acceptedFactorContext?.factorSnapshotDate
                ?? factorSnapshotDate,
        };
        const availableFactorDataContext = pendingFactorContext
            ? {
                factorModel: pendingFactorContext.factorModel,
                factorDataModel: pendingFactorContext.factorDataModel,
                factorSnapshotDate: pendingFactorContext.factorSnapshotDate,
            }
            : acceptedFactorDataContext;
        return {
            userUid: user?.uid,
            factorData: {
                status: watchlistStatus?.status === 'update_available'
                    ? (hasFactorDataUpdate(watchlistStatus) ? 'update_available' : 'current')
                    : watchlistStatus?.status ?? 'checking',
                loading: watchlistContextSync.updateInProgress,
                usingContext: factorDataDisplayContext(watchlistStatus?.saved) ?? acceptedFactorDataContext,
                availableContext: factorDataDisplayContext(watchlistStatus?.available) ?? availableFactorDataContext,
                detailText: watchlistContextSync.updateInProgress
                    ? watchlistContextSync.updateProgressMessage
                    : 'Factor data used by your Watchlist and Actual Portfolio. The current dataset remains active until an update completes.',
            },
            masterSecurityIdentity: {
                status: deriveMsiContextStatus(watchlistStatus),
            },
            watchlistActual: {
                contextStatus: watchlistStatus,
            },
            positionSizing: {
                status: posSizingDraftFreshnessSummary?.contextState === 'unavailable'
                    ? 'unavailable'
                    : posSizingDraftFreshnessSummary?.isStale
                        ? 'stale'
                        : posSizingContext?.freshness ?? 'checking',
                availability: posSizingContext?.availability ?? null,
                contextDigest: posSizingContext?.context_document_sha256 ?? null,
                summary: posSizingDraftFreshnessSummary,
            },
            optimizedPortfolios: (listOfWhatIfPortfolios || [])
                .filter((portfolio) => portfolio?.type === 'opt')
                .map((portfolio) => ({
                    id: portfolio.id,
                    name: portfolio.value,
                    parent: portfolio.baselineSourceMode,
                    status: portfolio.contextStatus?.status ?? null,
                    parentFreshness: portfolio.contextStatus?.parent_freshness ?? null,
                    materializationState:
                        portfolio.contextStatus?.materialization_state ?? null,
                    factorModel: portfolio.factorModel ?? null,
                    factorDataModel: portfolio.factorDataModel ?? null,
                    factorSnapshotDate: portfolio.factorSnapshotDate ?? null,
                    lastRefreshedAt: portfolio.lastRefreshedAt ?? null,
                    staleReasons: portfolio.contextStatus?.reasons
                        ?? portfolio.staleReasons
                        ?? [],
                    contextPresentation: portfolio.contextPresentation ?? null,
                })),
        };
    }, [
        acceptedFactorContext,
        activeRequestedFactorDataModel,
        activeSelectedFactorModel,
        factorSnapshotDate,
        listOfWhatIfPortfolios,
        pendingFactorContext,
        posSizingDraftFreshnessSummary,
        posSizingSyncStatus?.optimizer_context,
        watchlistContextSync?.status,
        watchlistContextSync.updateInProgress,
        watchlistContextSync.updateProgressMessage,
        user?.uid,
    ]);

    const handlePosSizingAutoSyncEnabledChange = useCallback((enabled) => {
        posSizingSyncPreferences.setEnabled(enabled);
        posSizingAutoSyncEnabledRef.current = Boolean(enabled);
        if (enabled) void requestPosSizingContextUpdate({ reason: 'auto_sync_enabled' });
        getRiskCommandsOrThrow(
            riskCommandsRef,
            'Dashboard.handlePosSizingAutoSyncEnabledChange',
            ['refreshSelectedOverview'],
        ).refreshSelectedOverview({ reason: 'pos_sizing_auto_sync_toggled' });
    }, [posSizingSyncPreferences.setEnabled, requestPosSizingContextUpdate]);

    useDashboardAppBarBridge({
        enabled: Boolean(user),
        handleFactorDataRefreshRequest,
        handleRiskContextRefreshRequest,
        handleFactorContextChanged,
        handlePortfolioGuideInteraction,
        onAutoRefreshEnabledChange: handleAutoRefreshEnabledChange,
        setPosSizingAutoSyncEnabled: handlePosSizingAutoSyncEnabledChange,
        setFactorDataAutoSyncEnabled,
        posSizingAutoSyncToggleBusy,
        uploadContext: appBarUploadContextWithFactorData,
        syncState: appBarSyncStateWithFactorData,
        contextLineage: appContextLineage,
    });
    const handleOpenPortfolioImports = useCallback(() => {
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent(APPBAR_UPLOAD_MODAL_OPEN_EVENT));
        }
    }, []);
    const { riskFactorsHeaderActionsProps } = useRiskFactorsHeaderActionsConfig({
        optimalPortfolioOptimizer,
        optimalPortfolioSummary,
        setOptimalPortfolioOptimizer,
        setOptimalPortfolioSummary,
        handleWhatIfPortfolioChangeSummary,
        listOfWhatIfPortfolios,
        fetchOptimizerPortfolios,
        setError,
        setShowPortfolioMetrics,
        clearOptimizedOverlay,
        unsavedOptimizerRun,
        setOptimizedRiskMetrics,
        refreshSavedOptimizedPortfolio,
        availableBetaSources,
        setOptimizedGroupRiskMetrics,
        setOptimalPortfolioSummaryLoaded,
        onBeforeSelectPortfolio: handleOverviewOptimizerRestorationRequest,
        onWatchlistRestorationStaged: handleOptimizerWatchlistRestorationStaged,
        posSizingSyncStatus,
        posSizingFreshnessSummary: posSizingDraftFreshnessSummary,
        onSyncPosSizingDraft: handleSyncRequiredPosSizingDraft,
        posSizingSyncDisabled: (
            actualPortfolioIsReady !== true
            || posSizingLoading
            || posSizingDraftSyncLoading
            || posSizingSyncStatusLoading
            || watchlistOperationActive
        ),
        posSizingSyncInProgress: (
            posSizingLoading
            || posSizingDraftSyncLoading
            || posSizingSyncStatusLoading
        ),
    });
    const riskFactorsHeaderActions = (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 1,
                flexWrap: 'wrap',
                maxWidth: '100%',
            }}
        >
            <RiskFactorsHeaderActions {...riskFactorsHeaderActionsProps} />
        </Box>
    );

    if (!user) {
        return <Typography>Please log in to view your dashboard.</Typography>;
    }

    const pageError = resolveDashboardPageError({
        activeView,
        error,
        factorContextUnavailable,
        watchlistContextStatus: watchlistContextSync.status,
    });
    const initialWatchlistValidationRequired = Boolean(
        factorContextUnavailable
        && watchlistContextSync.status?.requires_initial_validation === true,
    );

    return (
        <Box>
            <FactorDataUpdateDialog sync={watchlistContextSync} suppressed={factorDataDetailsOpen} />
            {factorDataRecoveryRequired && (
                <Alert
                    severity="info"
                    sx={{ mb: 2 }}
                    action={publishedFactorUpdate && (
                        <Button
                            color="inherit"
                            size="small"
                            onClick={watchlistContextSync.showPrompt}
                            disabled={watchlistContextSync.busy || watchlistOperationActive}
                        >
                            Review update
                        </Button>
                    )}
                >
                    {publishedFactorUpdate
                        ? 'Risk calculations are paused because the saved factor data is unavailable. Review the available update to restore them.'
                        : 'Risk calculations are paused because the saved factor data is unavailable. They will resume when factor data is restored.'}
                </Alert>
            )}
            <DashboardLoadedFactorChip
                activeView={activeView}
                userUid={user?.uid}
                scopeKey={loadedFactorScopeKey}
                loadedRiskContext={loadedCoreFactorContext}
                riskResultLoaded={loadedCoreFactorResult}
                posSizingWorkspace={posSizingAttributeWorkspaceHydration}
                posSizingLoading={posSizingLoading || !posSizingLoaded}
                optimizerFactorContext={loadedOptimizerFactorContext}
                optimizerResultLoaded={loadedOptimizerFactorResult}
            />
            {pageError && (
                <Typography color="error" mb={2}>
                    Error: {pageError}
                </Typography>
            )}

            {initialWatchlistValidationRequired && activeView !== 'watchlist' && (
                <Alert
                    severity="warning"
                    sx={{ mb: 2 }}
                    action={(
                        <Button
                            color="inherit"
                            size="small"
                            onClick={() => navigate('/dashboard/watchlist')}
                        >
                            Review Watchlist
                        </Button>
                    )}
                >
                    This Watchlist predates MSI-v2 context tracking. Its one-time validation
                    is being prepared for review; factor and risk views will become available
                    after you approve the validated Watchlist.
                </Alert>
            )}

            <PerformancePendingImportBanner
                show={showPerformancePendingBanner}
                pendingRunId={pendingPerformanceImport?.id}
                onReview={handleReviewPerformancePendingImport}
                onDismiss={setDismissedPerformancePendingRunId}
            />

            {activeView === 'overview' && (
                <DashboardOverviewSection
                    expectedActualScopeKey={actualRiskOverviewScopeKey}
                    expectedSelectedScopeKey={expectedSelectedRiskOverviewScopeKey}
                    calculationsLoading={calculationsLoading}
                    matricesLoading={matricesLoading}
                    calculations={calculations}
                    optimizedRiskMetrics={optimizedRiskMetrics}
                    groupRiskMetrics={groupRiskMetrics}
                    optimizedGroupRiskMetrics={optimizedGroupRiskMetrics}
                    groupRisksLoading={groupRisksLoading}
                    optimizedGroupRiskMetricsLoading={optimizedGroupRiskMetricsLoading}
                    optimalPortfolioSummaryLoaded={optimalPortfolioSummaryLoaded}
                    optimalPortfolioSummary={optimalPortfolioSummary}
                    selectedFactor={selectedFactor}
                    currentPage={currentPage}
                    rowsPerPage={rowsPerPage}
                    setCurrentPage={setCurrentPage}
                    handleSelectFactor={handleSelectFactor}
                    sedolsForFactor={sedolsForFactor}
                    tickersLoading={tickersLoading}
                    tickersError={tickersError}
                    combinedHoldings={combinedHoldings}
                    industries={industries}
                    watchlistAttributeSnapshot={watchlistAttributeSnapshot}
                    priceRefreshLoading={priceRefreshLoading}
                    priceSyncControl={priceSyncControl}
                    onPriceSync={handlePriceRefreshRequest}
                    betaSourceTransitionLoading={betaSourceTransitionLoading}
                    coreLoadVisualLoading={coreLoadVisualLoading}
                    compactMode={compactMode}
                    actualPortfolioIsReady={actualPortfolioIsReady}
                    positionsLoading={positionsLoading}
                    holdingsLoading={holdingsLoading}
                    riskFactorsHeaderActions={riskFactorsHeaderActions}
                    aum={aum}
                    aumLoading={aumLoading}
                    handleOpenPortfolioImports={handleOpenPortfolioImports}
                    showPortfolioImportGuide={showPortfolioImportGuide}
                    onDismissPortfolioImportGuide={dismissSetupGuide}
                    onOpenPortfolioEditor={() => {
                        handlePortfolioGuideInteraction();
                        setPortfolioEditorDialogOpen(true);
                    }}
                    onOpenPositionSizing={() => navigate('/dashboard/pos-sizing')}
                />
            )}

            <PersistentDashboardView
                key={`watchlist:${user?.uid || ''}`}
                active={activeView === 'watchlist'}
                mountWhenInactive={portfolioEditorDialogOpen}
            >
                <DashboardWatchlistSection
                    active={activeView === 'watchlist'}
                    user={user}
                    industries={industries}
                    portfolioEditor={watchlistPortfolioEditor}
                    portfolioDialogOpen={portfolioEditorDialogOpen}
                    onClosePortfolioDialog={() => setPortfolioEditorDialogOpen(false)}
                    watchlistAttributeSnapshot={watchlistAttributeSnapshot}
                    industriesLoading={industriesLoading}
                    coreLoadVisualLoading={coreLoadVisualLoading}
                    watchlistUploadBusy={watchlistUploadBusy}
                    watchlistPublicationRefreshBusy={Boolean(
                        watchlistPublicationState?.publishing
                    )}
                    watchlistPublicationNeedsAttention={
                        watchlistPublicationState?.lifecycle === 'needs_attention'
                    }
                    liveQuotesByCogniveId={liveQuotesByCogniveId}
                    liveQuotesLastUpdatedAt={liveQuotesLastUpdatedAt}
                    imputedWatchlistCogniveIds={imputedWatchlistCogniveIds}
                    ignoredWatchlistCogniveIds={ignoredWatchlistCogniveIds}
                    selectedFactorModel={activeSelectedFactorModel}
                    selectedBetaSource={selectedBetaSource}
                    factorContextUnavailable={factorContextUnavailable}
                    factorMatricesUnavailable={factorMatricesUnavailable}
                    watchlistUploadControl={watchlistUploadControl}
                    watchlistM365SyncControl={watchlistM365SyncControl}
                    watchlistReviewHandoff={watchlistReviewHandoff}
                    onWatchlistReviewHandoff={handleWatchlistReviewHandoff}
                    watchlistContextSync={watchlistContextSync}
                    showWatchlistUploadGuide={showWatchlistUploadGuide}
                    onDismissWatchlistGuide={handleWatchlistGuideInteraction}
                    newsByIdentity={newsByIdentity}
                    newsMoverThresholdPct={newsMoverThresholdPct}
                    newsLastUpdatedAt={newsLastUpdatedAt}
                    newsRecencyWindowHours={newsRecencyWindowHours}
                    newsReadArticleIds={newsReadArticleIds}
                    onNewsArticleRead={markNewsArticleRead}
                    deepDiveFocusAreas={deepDiveFocusAreas}
                    onLoadNewsDetails={loadNewsDetails}
                    newsArticlesByIdentity={newsArticlesByIdentity}
                    newsArticlesLoadingByIdentity={newsArticlesLoadingByIdentity}
                    newsArticlesErrorByIdentity={newsArticlesErrorByIdentity}
                    securityOperationStatus={securityOperationStatus}
                />
            </PersistentDashboardView>

            <PersistentDashboardView
                key={`pos-sizing:${user?.uid || ''}`}
                active={activeView === 'pos-sizing'}
            >
                <DashboardPosSizingSection
                    active={activeView === 'pos-sizing'}
                    loadError={posSizingLoadError}
                    onRetryLoad={() => fetchPosSizingData({ persistPortfolio: false })}
                    onSyncDraft={posSizingLoadSyncPrompt
                        ? () => handleSavedDraftSyncRequired(posSizingLoadSyncPrompt)
                        : undefined}
                    userUid={user?.uid}
                    contextUpdateState={contextUpdateState}
                    contextUpdateVersion={posSizingVersion}
                    onRetryContextUpdate={handleSyncPosSizingDraft}
                    posSizingData={posSizingData}
                    industries={industries}
                    setPosSizingData={setPosSizingData}
                    posSizingWorkspaceBusy={Boolean(
                        posSizingWorkspaceBusy
                        || (
                            activeView === 'pos-sizing'
                            && watchlistIsReady !== false
                            && !posSizingLoaded
                        )
                    )}
                    displayUnit={displayUnit}
                    maxPosSize={maxPosSize}
                    setMaxPosSize={setMaxPosSize}
                    normalVol={normalVol}
                    setNormalVol={setNormalVol}
                    defaultTargetVolPct={defaultTargetVolPct}
                    defaultTargetVolDollar={defaultTargetVolDollar}
                    defaultScaler={defaultScaler}
                    onPosSizingSaved={onPosSizingSaved}
                    handlePosSizingEditingChange={handlePosSizingEditingChange}
                    handlePosSizingSavingChange={handlePosSizingSavingChange}
                    handlePosSizingPendingChangesChange={handlePosSizingPendingChangesChange}
                    posSizingLinkedRowId={posSizingLinkedRowId}
                    posSizingLinkedRowRequestId={posSizingLinkedRowRequestId}
                    setPosSizingVisualizationRows={setPosSizingVisualizationRows}
                    posSizingImportsControl={posSizingImportsControl}
                    posSizingVisualizationRows={posSizingVisualizationRows}
                    handlePosSizingScatterPointSelect={handlePosSizingScatterPointSelect}
                    posSizingPublicationBaseRef={posSizingPublicationBaseRef}
                    attributeWorkspaceHydration={posSizingAttributeWorkspaceHydration}
                    onStartFromWatchlist={handleStartPosSizingFromWatchlist}
                    sizingSettingsCommandRef={posSizingSettingsCommandRef}
                    sizingDefaultsControl={(
                        <SizingDefaultsControl
                            currentValues={
                                posSizingStartingValues.startingValues
                                && Number.isFinite(maxPosSize)
                                && Number.isFinite(normalVol)
                                    ? {
                                        ...posSizingStartingValues.startingValues.current,
                                        max_pos_size: maxPosSize,
                                        normal_vol: normalVol,
                                    }
                                    : null
                            }
                            initialValues={posSizingStartingValues.startingValues?.initial ?? null}
                            canPickTargetVolDollar={
                                posSizingPublicationBaseRef.current?.portfolio_id != null
                                && posSizingData.some((row) => {
                                    const gmv = Number(row?.pos_gmv_dollar);
                                    return Number.isFinite(gmv) && gmv !== 0;
                                })
                            }
                            onSuggest={handleSuggestSizingDefaults}
                            onSave={handleSaveSizingDefaults}
                            disabled={posSizingTableSaving || posSizingLoading}
                        />
                    )}
                />
            </PersistentDashboardView>

            {activeView === 'optimizer' && (
                <DashboardOptimizerSection
                    setConstraints={setConstraints}
                    riskMetricsActPort={riskMetricsActPort}
                    riskMetricsOptPort={riskMetricsOptPort}
                    riskMetricsDeltaPort={riskMetricsDeltaPort}
                    displayedExpectedReturnPolicy={displayedExpectedReturnPolicy}
                    maxSharpeSkill={maxSharpeSkill}
                    setMaxSharpeSkill={setMaxSharpeSkill}
                    errorOptimization={errorOptimization}
                    optimizerQueueState={optimizerQueueState}
                    cancelQueuedOptimization={cancelQueuedOptimization}
                    setErrorOptimization={setErrorOptimization}
                    optimizerIsRunning={optimizerIsRunning}
                    optimizerRunBlocker={optimizerRunBlocker}
                    runOptimization={runOptimization}
                    metaOptimizer={metaOptimizer}
                    constraints={constraints}
                    gmvBudget={gmvBudget}
                    groupConstraints={groupConstraints}
                    setGroupConstraints={setGroupConstraints}
                    optimizerV2Capability={optimizerV2Capability}
                    optimizerAttributeCatalog={optimizerAttributeCatalog}
                    handleWhatIfPortfolioChange={handleWhatIfPortfolioChange}
                    optimalPortfolioOptimizer={optimalPortfolioOptimizer}
                    setOptimalPortfolioOptimizer={setOptimalPortfolioOptimizer}
                    optimalPortfolioSummary={optimalPortfolioSummary}
                    setOptimalPortfolioSummary={setOptimalPortfolioSummary}
                    listOfWhatIfPortfolios={listOfWhatIfPortfolios}
                    saveOptimizationAndWhatIfPortfolioData={saveOptimizationAndWhatIfPortfolioData}
                    constraintToggles={constraintToggles}
                    setConstraintToggles={setConstraintToggles}
                    showPortfolioMetrics={showPortfolioMetrics}
                    tickerData={tickerData}
                    setTickerData={setTickerData}
                    objective={objective}
                    setObjective={setObjective}
                    fetchOptimizerPortfolios={fetchOptimizerPortfolios}
                    setShowPortfolioMetrics={setShowPortfolioMetrics}
                    clearOptimizedOverlay={clearOptimizedOverlay}
                    unsavedOptimizerRun={unsavedOptimizerRun}
                    receiptDisplayActive={receiptDisplayActive}
                    resultStale={resultStale}
                    detachOptimizerResult={detachOptimizerResult}
                    resetOptimizerLimits={resetOptimizerLimits}
                    setOptimizedRiskMetrics={setOptimizedRiskMetrics}
                    optStatus={optStatus}
                    viewMode={viewMode}
                    setViewMode={setViewMode}
                    risksByFactor={risksByFactor}
                    constraintEvaluation={constraintEvaluation}
                    resultIssuedAt={resultIssuedAt}
                    solverStatus={solverStatus}
                    factorNames={factorNames}
                    handleAddConstraint={handleAddConstraint}
                    handleRemoveConstraint={handleRemoveConstraint}
                    matricesLoading={matricesLoading}
                    viewModeLoading={optimizerBaselineLoading}
                    finalTickerDataFlag={finalTickerDataFlag}
                    resetOptimizerDefaults={resetOptimizerDefaults}
                    refreshSavedOptimizedPortfolio={refreshSavedOptimizedPortfolio}
                    posSizingSyncStatus={posSizingSyncStatus}
                    onSyncPosSizingDraft={handleSyncRequiredPosSizingDraft}
                    posSizingOptimizerContextControl={posSizingOptimizerContextControl}
                    onWatchlistRestorationStaged={handleOptimizerWatchlistRestorationStaged}
                />
            )}

            {activeView === 'performance' && (
                <DashboardPerformanceSection
                    factorDatasetSelectionEnabled={factorDatasetCatalogue.enabled}
                    factorContext={displayedPerformanceContext}
                    publicationPending={Boolean(publicationPresentationPending)}
                    onRegisterPublicationPreparation={registerPublicationPreparation}
                    factorSliceScopeKey={loadedFactorScopeKey}
                    initialContextPending={!startupSecondaryLoadsReady
                        || !String(lastCoreLoadKeyRef.current || '').startsWith(`${user?.uid}|`)
                        || displayedPerformanceContext == null}
                    initialContextFailed={Boolean(!displayedPerformanceContext
                        && (pageError || watchlistContextSync.currentStatus?.status === 'read_error'))}
                    activeView={activeView}
                    optimalPortfolioOptimizer={optimalPortfolioOptimizer}
                    optimalPortfolioSummary={optimalPortfolioSummary}
                    setOptimalPortfolioOptimizer={setOptimalPortfolioOptimizer}
                    setOptimalPortfolioSummary={setOptimalPortfolioSummary}
                    listOfWhatIfPortfolios={listOfWhatIfPortfolios}
                    fetchOptimizerPortfolios={fetchOptimizerPortfolios}
                    setError={setError}
                    setShowPortfolioMetrics={setShowPortfolioMetrics}
                    clearOptimizedOverlay={clearOptimizedOverlay}
                    unsavedOptimizerRun={unsavedOptimizerRun}
                    setOptimizedRiskMetrics={setOptimizedRiskMetrics}
                    matricesLoading={matricesLoading}
                    actualPortfolioIsReady={actualPortfolioIsReady}
                    watchlistIsReady={watchlistIsReady}
                    actualPortfolioVersion={actualPortfolioVersion}
                    watchlistVersion={watchlistVersion}
                    userUid={user?.uid}
                    onWatchlistRestorationStaged={handleOptimizerWatchlistRestorationStaged}
                    compareExploreColumns={compareExploreColumns}
                    setCompareExploreColumns={setCompareExploreColumns}
                />
            )}



            <PerformancePendingImportDialog
                open={performancePendingReviewOpen}
                applying={performancePendingReviewApplying}
                error={performancePendingReviewError}
                run={performancePendingReviewRun}
                onApply={handleApplyPerformancePendingImport}
                onIgnore={handleIgnorePerformancePendingImport}
                onClose={() => {
                    if (performancePendingReviewApplying) return;
                    setPerformancePendingReviewOpen(false);
                }}
            />

            <MsiOutboundBarraResolutionDialog
                open={activeOutboundResolutionDialogOpen}
                identityResolution={activeOutboundResolutionIdentityResolution}
                onClose={closeActiveOutboundResolutionDialog}
                onSaveAndRetry={retryActiveOutboundResolution}
            />

            <OptimizerWatchlistRestorationDialog
                open={Boolean(overviewOptimizerRestorationPortfolio)}
                portfolio={overviewOptimizerRestorationPortfolio}
                onClose={() => setOverviewOptimizerRestorationPortfolio(null)}
                onError={handleOptimizerRestorationError}
                onDeleted={handleOptimizerWatchlistRestorationDeleted}
                onStaged={handleOptimizerWatchlistRestorationStaged}
                fetchOptimizerPortfolios={fetchOptimizerPortfolios}
            />

            {posSizingStartingValues.onboardingPrompt ? (
                <PosSizingStartingValuesDialog
                    key={posSizingStartingValues.onboardingPrompt.source}
                    picked={posSizingStartingValues.onboardingPrompt.picked}
                    suggestion={posSizingStartingValues.onboardingPrompt.suggestion}
                    aum={posSizingStartingValues.onboardingPrompt.aum}
                    aumStored={posSizingStartingValues.onboardingPrompt.aumStored}
                    aumLoading={posSizingStartingValues.onboardingPrompt.aumLoading}
                    aumError={posSizingStartingValues.onboardingPrompt.aumError}
                    onRetryAum={posSizingStartingValues.retryOnboardingAum}
                    onChoose={posSizingStartingValues.choosePromptValues}
                />
            ) : null}

            <DraftFreshnessHeaderControl
                summary={posSizingSyncRequiredSummary}
                onSyncClick={handleSyncRequiredPosSizingDraft}
                syncDisabled={
                    actualPortfolioIsReady !== true
                    || posSizingLoading
                    || posSizingDraftSyncLoading
                    || posSizingSyncStatusLoading
                    || watchlistOperationActive
                }
                syncInProgress={
                    posSizingLoading
                    || posSizingDraftSyncLoading
                    || posSizingSyncStatusLoading
                }
                detailsOpen={Boolean(posSizingSyncRequiredPrompt) && activeView === 'pos-sizing'}
                onDetailsOpenChange={(open) => {
                    if (!open) setPosSizingSyncRequiredPrompt(null);
                }}
                triggerVariant="dialog-only"
            />

            <DashboardSnackbars
                snackbarOpen={snackbarOpen}
                setSnackbarOpen={setSnackbarOpen}
                snackbarSeverity={snackbarSeverity}
                snackbarMessage={snackbarMessage}
                aumSnackOpen={aumSnackOpen}
                setAumSnackOpen={setAumSnackOpen}
                aumSnackMessage={aumSnackMessage}
            />

        </Box>
    );
}

export default Dashboard;
